/**
 * Server-state hooks (TanStack Query). All endpoints are proxied via Vite to the
 * FastAPI backend during dev, and via Caddy/Tailscale in prod. Cookies are sent
 * with `credentials: 'include'` so the JWT cookie (Phase 2) flows.
 */

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'
import {
  drainOutbox,
  enqueueOutbox,
  listOutbox,
  noteAgentChatOriginReachable,
  outboxAttachmentPreviewUrl,
  type OutboxItem,
} from './outbox'
import {
  getSnapshotChat,
  getSnapshotChats,
  getRenderableSnapshotMessages,
  getSnapshotTopics,
  loadLocalChat,
  loadLocalChats,
  loadFreshLocalMessages,
  loadArchivedMessageWindow,
  loadOlderArchivedMessages,
  loadRenderableLocalMessages,
  loadLocalTopics,
  markLocalReadState,
  saveLocalChat,
  saveLocalChats,
  saveLocalMessages,
  saveLocalTopics,
} from './localReadStore'
import { compactTopicsForRecentDoneScope } from './topicFilterCounts'
import { mergeTopicUpdates } from './topicListUpdates'
import { mergeVisibleMessages } from './messageMerge'
import { selectTopicReadWarmTargets } from './topicReadWarmTargets'
import { selectTopicListWarmTargets } from './topicListWarmTargets'
import { messageIsMarkAuthoredInbound } from './messageVisibility'
import type {
  Agent,
  Attachment,
  Chat,
  ClaudeEffort,
  CodexAccountMode,
  CodexAvailability,
  CodexEffort,
  CodexModel,
  Message,
  ProgressKind,
  ToolActivity,
  Topic,
} from './apiModels'
export type {
  Agent,
  Attachment,
  Chat,
  ClaudeEffort,
  CodexAccount,
  CodexAccountMode,
  CodexAccountState,
  CodexAvailability,
  CodexEffort,
  CodexModel,
  CommitmentTopicItem,
  Message,
  ProgressKind,
  ToolActivity,
  Topic,
} from './apiModels'

export type WritableAgent = 'claude' | 'codex' | 'codex-secondary'
export type AskAnotherAgent = Exclude<Agent, 'cursor' | 'grok'> | 'pro'

export type ChatGptProHandoff = {
  provider: 'chatgpt-pro'
  url: string
  prompt: string
  warning: string
}

export type WorkMonitorItem = {
  kind: 'chat' | 'topic'
  chat_id: string
  topic_id: string | null
  chat_display_name: string | null
  title: string
  agent: Agent
  category: string | null
  status: 'working' | 'queued'
  started_at: number
  age_seconds: number
  last_active_at: number | null
  active_message_count: number
  queued_count: number
  dispatched_count: number
  chat_worker_capacity: number
  preview: string | null
  last_progress_at: number | null
  last_progress_kind: ProgressKind | null
}

export type WorkMonitor = {
  summary: {
    chat_count: number
    manual_chat_count: number
    auto_chat_count: number
    worker_capacity: number
    busy_lane_count: number
    queued_message_count: number
    dispatched_message_count: number
    active_item_count: number
    max_workers_per_chat: number
    default_max_workers_per_chat: number
    high_throughput_chat_ids: string[]
  }
  items: WorkMonitorItem[]
  now: number
}

export type SendAttachment = {
  id: string
  kind: Attachment['kind']
  filename: string | null
  mime_type: string | null
  size_bytes: number | null
  local_path?: string | null
  preview_url?: string
  file?: File | Blob | null
}

export type SendMessageInput = {
  text: string
  force?: boolean
  topic_id?: string | null
  model_override?: string | null
  effort_override?: string | null
  attachments?: SendAttachment[]
  client_id?: string
  reply_to_message_id?: string | null
}

export type MessageTailScope = {
  chat_id: string
  topic_id: string | null
  message_scope_version?: number | null
  messages: Message[]
}

export type StartupBundle = {
  chats: Chat[]
  work_monitor: WorkMonitor
  now: number
}

export type StartupWarmBundle = {
  message_tails: MessageTailScope[]
  topic_lists: Array<{ chat_id: string; topics: Topic[] }>
  now: number
}

export type SetupManifest = {
  edition?: string
  ready?: boolean
  ui?: {
    hide_groups?: boolean
    visible_group_ids?: string[]
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    // 401 = no/expired session → bounce to /login (preserve where they
    // were trying to go via ?next=). Skip the redirect if already on
    // /login to avoid a loop.
    if (res.status === 401 && typeof window !== 'undefined') {
      const here = window.location.pathname + window.location.search
      if (!here.startsWith('/login')) {
        const url = new URL('/login', window.location.origin)
        url.searchParams.set('next', here)
        window.location.replace(url.toString())
      }
    }
    const text = await res.text().catch(() => '')
    throw new Error(formatApiErrorMessage(res, text))
  }
  // A successful read is stronger evidence than navigator.onLine that the
  // private Android/Tailscale route is usable. Wake any durable prompt that
  // was stranded while the route was unavailable.
  const method = (init?.method ?? 'GET').toUpperCase()
  if (method === 'GET' || method === 'HEAD') noteAgentChatOriginReachable()
  return (await res.json()) as T
}

function formatApiErrorMessage(res: Response, bodyText: string): string {
  if (res.status === 401) return 'Session expired. Reopening login.'
  if (res.status >= 500) return `Server error (${res.status}). Please try again.`

  const detail = extractApiErrorDetail(bodyText)
  const suffix = detail ? `: ${detail}` : ''
  return `Request failed (${res.status})${suffix}`
}

function extractApiErrorDetail(bodyText: string): string {
  const trimmed = bodyText.trim()
  if (!trimmed) return ''
  try {
    const parsed = JSON.parse(trimmed) as { detail?: unknown }
    const detail = parsed.detail
    if (typeof detail === 'string') return detail.slice(0, 200)
    if (Array.isArray(detail)) return 'Invalid request.'
  } catch {
    // Non-JSON 4xx bodies are already user-actionable enough when shortened.
  }
  return trimmed.slice(0, 200)
}

export function useSetupManifest() {
  return useQuery({
    queryKey: ['setup-manifest'],
    queryFn: () => api<SetupManifest>('/api/setup/manifest'),
    staleTime: 60_000,
    retry: 1,
  })
}

type UseChatsOptions = {
  enabled?: boolean
  poll?: boolean
  warmReadModels?: boolean
}

let _startupBundlePending: Promise<StartupBundle> | null = null
let _startupWarmBundlePending: Promise<StartupWarmBundle> | null = null

export function useChats(options: boolean | UseChatsOptions = {}) {
  const qc = useQueryClient()
  const enabled = typeof options === 'boolean' ? options : (options.enabled ?? true)
  const poll = typeof options === 'boolean' ? options : options.poll !== false
  const warmReadModels = typeof options === 'boolean' ? false : options.warmReadModels === true
  const warmReadModelsForChats = useCallback((chats: Chat[]): void => {
    if (!warmReadModels) return
    void loadStartupWarmBundle()
      .then((bundle) => applyStartupWarmBundle(qc, bundle))
      .catch(() => {
        preloadAllChatMessageTails(qc)
        scheduleTopicListWarm(qc, chats)
      })
    scheduleRecentChatReadWarm(qc, chats)
  }, [qc, warmReadModels])
  return useQuery({
    queryKey: ['chats'],
    queryFn: async () => {
      // placeholderData already paints the hydrated IndexedDB snapshot on the
      // first frame. A mounted/refetched list must never return that older
      // snapshot again: doing so temporarily replaces current server truth and
      // makes filtered rows disappear until the live bundle arrives.
      if (!canTryNetwork()) {
        const cached = await loadLocalChats()
        if (cached) {
          warmReadModelsForChats(cached)
          return cached
        }
        throw new Error('Offline. Chat list unavailable.')
      }
      const bundle = await loadStartupBundle()
      await applyStartupBundle(qc, bundle)
      warmReadModelsForChats(bundle.chats)
      return bundle.chats
    },
    enabled,
    // Synchronous render path: when the IDB snapshot is already in memory
    // (after hydrateLocalReadSnapshots ran at boot) the chat-list paints in
    // the same frame as App.tsx mount, no spinner. queryFn still runs in the
    // background to refresh against the live backend.
    placeholderData: () => getSnapshotChats() ?? undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: poll ? 30_000 : false,
    networkMode: 'always',
  })
}

export type EngagedTopic = {
  pending_approval?: Chat['pending_approval']
  id: string
  chat_id: string
  title: string | null
  chat_display_name: string | null
  agent: Chat['agent']
  last_activity_at: number
  working: number
  queued: number
  unread: number
  snoozed_until: number | null
  scheduled_recheck_at: number | null
  participation_message_id: string
}

export function useEngagedTopics(enabled: boolean) {
  return useQuery({
    queryKey: ['engaged-topics'],
    networkMode: 'always',
    refetchOnReconnect: 'always',
    queryFn: () => api<EngagedTopic[]>('/api/topics/engaged', { signal: AbortSignal.timeout(15_000) }),
    enabled,
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: 'always',
  })
}

export function useMarkEngagedTopicDone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (topic: Pick<EngagedTopic, 'chat_id' | 'id' | 'participation_message_id'>) => (
      api<{ ok: boolean }>(`/api/chats/${topic.chat_id}/topics/${topic.id}/engagement-done`, {
        method: 'POST',
        signal: AbortSignal.timeout(15_000),
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participation_message_id: topic.participation_message_id }),
      })
    ),
    retry: 1,
    onSuccess: (_result, topic) => {
      qc.setQueryData<EngagedTopic[]>(['engaged-topics'], (current) => current?.filter((row) => (
        row.id !== topic.id || row.participation_message_id !== topic.participation_message_id
      )))
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['engaged-topics'] }),
  })
}

export function useWorkMonitor(enabled = true) {
  return useQuery({
    queryKey: ['work-monitor'],
    queryFn: () => api<WorkMonitor>('/api/work-monitor'),
    enabled,
    staleTime: 5_000,
    refetchInterval: 30_000,
  })
}

function canTryNetwork(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine
}

function refreshCachedQuery<T>(
  qc: QueryClient,
  key: readonly unknown[],
  fetcher: () => Promise<T>,
  saver?: (value: T) => Promise<void>,
): void {
  if (!canTryNetwork()) return
  void fetcher()
    .then(async (value) => {
      qc.setQueryData(key, value)
      if (saver) await saver(value)
    })
    .catch(() => {
      // Cached data remains the visible read model; the normal retry/refetch
      // paths will try again later.
    })
}

function loadStartupBundle(): Promise<StartupBundle> {
  if (!canTryNetwork()) return Promise.reject(new Error('Offline. Startup bundle unavailable.'))
  if (!_startupBundlePending) {
    _startupBundlePending = api<StartupBundle>('/api/startup-bundle')
      .finally(() => {
        _startupBundlePending = null
      })
  }
  return _startupBundlePending
}

function loadStartupWarmBundle(): Promise<StartupWarmBundle> {
  if (!canTryNetwork()) return Promise.reject(new Error('Offline. Startup warm bundle unavailable.'))
  if (!_startupWarmBundlePending) {
    _startupWarmBundlePending = api<StartupWarmBundle>('/api/startup-warm-bundle')
      .finally(() => {
        _startupWarmBundlePending = null
      })
  }
  return _startupWarmBundlePending
}

async function applyStartupBundle(qc: QueryClient, bundle: StartupBundle): Promise<void> {
  await saveLocalChats(bundle.chats)
  qc.setQueryData(['chats'], bundle.chats)
  qc.setQueryData(['work-monitor'], bundle.work_monitor)
}

async function applyStartupWarmBundle(qc: QueryClient, bundle: StartupWarmBundle): Promise<void> {
  if (bundle.message_tails.length > 0) {
    await Promise.all(bundle.message_tails.map(async (scope) => {
      const messages = normalizeUnreadAfterReadReceipt(scope.chat_id, scope.topic_id, scope.messages)
      await saveLatestWindowReadModel(scope.chat_id, scope.topic_id, messages)
      const withOutbox = await mergeOutboxMessages(messages, scope.chat_id, scope.topic_id)
      qc.setQueryData<Message[]>(
        messagesKey(scope.chat_id, scope.topic_id),
        (current) => mergeLatestWindowRefresh(scope.chat_id, scope.topic_id, withOutbox, current),
      )
    }))
    _allChatTailWarmAt = Date.now()
  }

  await Promise.all(bundle.topic_lists.map(async (entry) => {
    await saveLocalTopics(entry.chat_id, entry.topics)
    qc.setQueryData(['topics', entry.chat_id], entry.topics)
    _topicListWarmAt.set(entry.chat_id, Date.now())
    preloadRecentTopicMessages(qc, entry.chat_id, entry.topics)
  }))
}

export function useChat(chatId: string | undefined) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['chat', chatId],
    queryFn: async () => {
      const cached = chatId ? await loadLocalChat(chatId) : null
      if (cached && chatId) {
        refreshCachedQuery(
          qc,
          ['chat', chatId],
          () => api<Chat>(`/api/chats/${chatId}`),
          saveLocalChat,
        )
        return cached
      }
      const live = await api<Chat>(`/api/chats/${chatId}`)
      await saveLocalChat(live)
      return live
    },
    enabled: !!chatId,
    // Instant header — pull from the IDB snapshot first (survives a cold tab),
    // then the chats list cache, then the per-chat snapshot row from a prior
    // visit. Any of those eliminates the "Loading chat…" full-pane on chat
    // open even when the placeholderData lookup is the user's first action of
    // the session.
    placeholderData: () => {
      if (!chatId) return undefined
      const snap = getSnapshotChat(chatId)
      if (snap) return snap
      const list = qc.getQueryData<Chat[]>(['chats']) ?? getSnapshotChats()
      return list?.find((c) => c.id === chatId) as Chat | undefined
    },
    // Header shows live lane status (Working / Connecting / Offline). The
    // primary update path is push: WS events invalidate ['chat', chatId]
    // when the lane changes state. refetchInterval is the safety net so
    // a missed/dropped WS event can never leave the header stuck (which
    // is what made Codex #3 read "Offline" while the agent was happily
    // replying).
    staleTime: 5_000,
    refetchOnMount: 'always',
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    gcTime: 30 * 60_000,        // keep cached chat detail for 30 min
    networkMode: 'always',
  })
}

export function messagesKey(chatId: string | undefined, topicId?: string | null) {
  return ['messages', chatId, topicId ?? null] as const
}

export const MESSAGES_INITIAL_FETCH_LIMIT = 10
const MESSAGES_FETCH_LIMIT = 500
const MESSAGES_OLDER_PAGE_LIMIT = 20
const MESSAGE_TAIL_PRELOAD_LIMIT = MESSAGES_INITIAL_FETCH_LIMIT
const READ_WARM_MAX_CONCURRENT = 4
const RECENT_TOPIC_READ_WARM_LIMIT = 3
const RECENT_READ_WARM_INITIAL_DELAY_MS = 0
const RECENT_READ_WARM_STEP_MS = 40
const READ_WARM_STALE_MS = 10 * 60_000
const ALL_CHAT_TAIL_WARM_STALE_MS = 30_000
const TOPIC_LIST_WARM_STALE_MS = 5 * 60_000
const TOPIC_LIST_WARM_INITIAL_DELAY_MS = 75
const TOPIC_LIST_WARM_STEP_MS = 125
const NOTIFICATION_READ_WARM_SUSPEND_MS = 8_000
const TOPICS_RECENT_DONE_DAYS = 3
const TOPICS_RECENT_DONE_LIMIT = 30
const X_LINKS_CHAT_ID = 'pwa-auto-x-links'
const X_LINKS_RECENT_DONE_LIMIT = 10
const _readWarmAt = new Map<string, number>()
const _readWarmVersions = new Map<string, string>()
const _readWarmPending = new Set<string>()
const _topicListWarmAt = new Map<string, number>()
const _topicListWarmPending = new Set<string>()
const _messagePreloadPending = new Map<string, Promise<void>>()
const _readWarmQueue: Array<() => Promise<void>> = []
let _allChatTailWarmAt = 0
let _allChatTailWarmPending: Promise<void> | null = null
let _readWarmActive = 0
let _topicWarmChain: Promise<void> = Promise.resolve()
let _readWarmSuspendedUntil = 0
const _expandedMessageReadModelScopes = new Set<string>()
const _retainedOpenMessageReadModelScopes = new Set<string>()

function readWarmersSuspended(): boolean {
  return Date.now() < _readWarmSuspendedUntil
}

function readWarmResumeDelay(): number {
  return Math.max(50, _readWarmSuspendedUntil - Date.now() + 50)
}

export function suspendReadModelWarmers(durationMs = NOTIFICATION_READ_WARM_SUSPEND_MS): void {
  _readWarmSuspendedUntil = Math.max(_readWarmSuspendedUntil, Date.now() + durationMs)
}

function messagesPath(
  chatId: string,
  topicId?: string | null,
  aroundMessageId?: string | null,
  limit = MESSAGES_INITIAL_FETCH_LIMIT,
  resumeAnchor?: 'latest_inbound' | null,
  beforeMessageId?: string | null,
) {
  const params = new URLSearchParams({
    since: '0',
    limit: String(limit),
    repair_documents: '0',
  })
  if (topicId) params.set('topic_id', topicId)
  if (aroundMessageId) params.set('around_message_id', aroundMessageId)
  if (resumeAnchor) params.set('resume_anchor', resumeAnchor)
  if (beforeMessageId) params.set('before_message_id', beforeMessageId)
  return `/api/chats/${chatId}/messages?${params.toString()}`
}

export type ToolActivityInput = {
  message_id: string
  seq: number
  tool_input: unknown
}

export type ToolActivitiesPayload = {
  message_id: string
  activities: ToolActivity[]
  next_after_seq: number
  has_more: boolean
}

export function getToolActivities(
  chatId: string,
  messageId: string,
  afterSeq = 0,
  limit = 50,
): Promise<ToolActivitiesPayload> {
  const params = new URLSearchParams({
    after_seq: String(afterSeq),
    limit: String(limit),
  })
  return api<ToolActivitiesPayload>(
    `/api/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}` +
    `/activities?${params.toString()}`,
  )
}

export function getToolActivityInput(
  chatId: string,
  messageId: string,
  seq: number,
): Promise<ToolActivityInput> {
  return api<ToolActivityInput>(
    `/api/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}` +
    `/activities/${encodeURIComponent(String(seq))}/input`,
  )
}

function notificationOpenBundlePath(
  chatId: string,
  topicId?: string | null,
  messageId?: string | null,
) {
  const params = new URLSearchParams()
  if (topicId) params.set('topic_id', topicId)
  if (messageId) params.set('message_id', messageId)
  const query = params.toString()
  return `/api/chats/${encodeURIComponent(chatId)}/notification-open-bundle${query ? `?${query}` : ''}`
}

function topicsPath(chatId: string, includeOldDone = false): string {
  const params = new URLSearchParams({
    done_scope: includeOldDone ? 'all' : 'recent',
  })
  if (!includeOldDone) {
    params.set('recent_done_days', String(TOPICS_RECENT_DONE_DAYS))
    params.set('recent_done_limit', String(
      chatId === X_LINKS_CHAT_ID ? X_LINKS_RECENT_DONE_LIMIT : TOPICS_RECENT_DONE_LIMIT,
    ))
    if (chatId === X_LINKS_CHAT_ID) params.set('view', 'compact')
  }
  return `/api/chats/${chatId}/topics?${params.toString()}`
}

function chatActivity(chat: Chat): number {
  return chat.last_activity_at ?? chat.last_agent_reply_at ?? chat.created_at ?? 0
}

function chatReadWarmPriority(chat: Chat): number {
  if ((chat.active_work_depth ?? 0) > 0 || (chat.queue_depth ?? 0) > 0) return 0
  if (chat.type === 'user-driven') return 1
  if ((chat.unread ?? 0) > 0) return 2
  return 3
}

function shouldWarmIndividualChat(chat: Chat): boolean {
  if (chat.type === 'auto-spawn') return Boolean(chat.single_unread_topic_id)
  return (chat.active_work_depth ?? 0) > 0 || (chat.queue_depth ?? 0) > 0 || (chat.unread ?? 0) > 0
}

function topicsForMode(topics: Topic[], includeOldDone: boolean): Topic[] {
  return includeOldDone ? topics : compactTopicsForRecentDoneScope(topics, {
    recentDoneDays: TOPICS_RECENT_DONE_DAYS,
    recentDoneLimit: TOPICS_RECENT_DONE_LIMIT,
  })
}

function messageScopeKey(chatId: string, topicId: string | null): string {
  return `${chatId}:${topicId ?? ''}`
}

function messageHistoryScopeKey(chatId: string, topicId: string | null): string {
  return `${chatId}::${topicId ?? '__all__'}`
}

export function clearExpandedMessageReadModel(chatId: string, topicId: string | null): void {
  const scope = messageScopeKey(chatId, topicId)
  _expandedMessageReadModelScopes.delete(scope)
  _retainedOpenMessageReadModelScopes.delete(scope)
}

function markExpandedMessageReadModel(chatId: string, topicId: string | null): void {
  _expandedMessageReadModelScopes.add(messageScopeKey(chatId, topicId))
}

export function retainOpenMessageReadModel(chatId: string, topicId: string | null): void {
  _retainedOpenMessageReadModelScopes.add(messageScopeKey(chatId, topicId))
}

function retainedOpenMessageScopes(): Array<{ chatId: string; topicId: string | null }> {
  return Array.from(new Set([
    ..._expandedMessageReadModelScopes,
    ..._retainedOpenMessageReadModelScopes,
  ])).map((scope) => {
    const separator = scope.indexOf(':')
    return {
      chatId: separator >= 0 ? scope.slice(0, separator) : scope,
      topicId: separator >= 0 && scope.slice(separator + 1) ? scope.slice(separator + 1) : null,
    }
  }).filter((scope) => scope.chatId.length > 0)
}

function scopeHasExpandedMessageReadModel(chatId: string, topicId: string | null): boolean {
  const scope = messageScopeKey(chatId, topicId)
  return (
    _expandedMessageReadModelScopes.has(scope) ||
    _retainedOpenMessageReadModelScopes.has(scope)
  )
}

function latestWindowReadModel(
  chatId: string,
  topicId: string | null,
  messages: Message[],
  limit = MESSAGES_INITIAL_FETCH_LIMIT,
): Message[] {
  return scopeHasExpandedMessageReadModel(chatId, topicId)
    ? messages
    : normalOpenMessageWindow(messages, limit)
}

async function saveLatestWindowReadModel(
  chatId: string,
  topicId: string | null,
  messages: Message[],
  limit = MESSAGES_INITIAL_FETCH_LIMIT,
): Promise<void> {
  const preserveOpenReadModel = scopeHasExpandedMessageReadModel(chatId, topicId)
  await saveLocalMessages(
    chatId,
    topicId,
    preserveOpenReadModel ? messages : normalOpenMessageWindow(messages, limit),
    { merge: preserveOpenReadModel },
  )
}

type MessageScopeVersions = {
  chat?: number | null
  topic?: number | null
}

type ReadStatePatch = {
  topicId?: string | null
  allTopics?: boolean
  markedRead?: number
}

type ReadReceiptOverlay = {
  chatId: string
  topicId: string | null
  allTopics: boolean
  markedRead?: number
  chatVersion: number | null
  topicVersion: number | null
  createdAt: number
}

const READ_RECEIPT_OVERLAY_TTL_MS = 2 * 60_000
const _readReceiptOverlays: ReadReceiptOverlay[] = []

function finiteScopeVersion(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function messageBatchScopeVersion(messages: Message[]): number | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const version = finiteScopeVersion(messages[index]?.message_scope_version)
    if (version !== null) return version
  }
  return null
}

function forgetExpiredReadReceiptOverlays(): void {
  const cutoff = Date.now() - READ_RECEIPT_OVERLAY_TTL_MS
  while (_readReceiptOverlays.length > 0 && _readReceiptOverlays[0].createdAt < cutoff) {
    _readReceiptOverlays.shift()
  }
}

function rememberReadReceiptOverlay(
  chatId: string,
  patch: ReadStatePatch,
  versions?: MessageScopeVersions | null,
): void {
  forgetExpiredReadReceiptOverlays()
  _readReceiptOverlays.push({
    chatId,
    topicId: patch.topicId ?? null,
    allTopics: patch.allTopics === true,
    markedRead: patch.markedRead,
    chatVersion: finiteScopeVersion(versions?.chat),
    topicVersion: finiteScopeVersion(versions?.topic),
    createdAt: Date.now(),
  })
  if (_readReceiptOverlays.length > 20) {
    _readReceiptOverlays.splice(0, _readReceiptOverlays.length - 20)
  }
}

function readOverlayVersionForQuery(
  overlay: ReadReceiptOverlay,
  queryTopicId: string | null,
): number | null {
  if (queryTopicId) {
    return overlay.topicId === queryTopicId ? overlay.topicVersion : null
  }
  return overlay.chatVersion
}

function readOverlayClearsMessage(
  overlay: ReadReceiptOverlay,
  queryTopicId: string | null,
  message: Message,
): boolean {
  if (overlay.allTopics) return true
  if (overlay.topicId) return message.topic_id === overlay.topicId
  if (queryTopicId) return false
  return true
}

function normalizeUnreadAfterReadReceipt(
  chatId: string,
  queryTopicId: string | null,
  messages: Message[],
): Message[] {
  if (messages.length === 0 || _readReceiptOverlays.length === 0) return messages
  forgetExpiredReadReceiptOverlays()
  const batchVersion = messageBatchScopeVersion(messages)
  let changed = false
  const next = messages.map((message) => {
    if (!message.unread) return message
    const shouldClear = _readReceiptOverlays.some((overlay) => {
      if (overlay.chatId !== chatId) return false
      const requiredVersion = readOverlayVersionForQuery(overlay, queryTopicId)
      if (
        requiredVersion !== null &&
        batchVersion !== null &&
        batchVersion >= requiredVersion
      ) return false
      return readOverlayClearsMessage(overlay, queryTopicId, message)
    })
    if (!shouldClear) return message
    changed = true
    return { ...message, unread: false }
  })
  return changed ? next : messages
}

function applyReadStateToQueryCache(
  qc: QueryClient,
  chatId: string,
  patch: ReadStatePatch = {},
): void {
  const topicId = patch.topicId ?? null
  const allTopics = patch.allTopics === true
  qc.setQueryData<Array<Pick<Topic, 'id' | 'unread'> & { chat_id: Chat['id'] }>>(['engaged-topics'], (current) =>
    current?.map((topic) => topic.chat_id === chatId && (allTopics || topic.id === topicId)
      ? { ...topic, unread: 0 }
      : topic),
  )
  const topics = qc.getQueryData<Topic[]>(['topics', chatId])
  const topicUnread = topicId
    ? topics?.find((topic) => topic.id === topicId)?.unread
    : undefined
  const markedRead = typeof patch.markedRead === 'number' && Number.isFinite(patch.markedRead)
    ? Math.max(0, Math.floor(patch.markedRead))
    : undefined
  const cachedChat = (
    qc.getQueryData<Chat>(['chat', chatId])
    ?? qc.getQueryData<Chat[]>(['chats'])?.find((chat) => chat.id === chatId)
  )
  const topicBackedChat = (topics?.length ?? 0) > 0 || cachedChat?.type === 'auto-spawn'
  const remainingTopicUnread = (topics ?? []).reduce(
    (sum, topic) => sum + (topic.unread ?? 0),
    0,
  )

  qc.setQueryData<Chat[]>(['chats'], (cur) =>
    (cur ?? []).map((chat) => {
      if (chat.id !== chatId) return chat
      if (allTopics) return { ...chat, unread: 0 }
      if (!topicId) {
        if (!topicBackedChat) return { ...chat, unread: 0 }
        if (typeof markedRead !== 'number') return chat
        return { ...chat, unread: Math.max(remainingTopicUnread, (chat.unread ?? 0) - markedRead) }
      }
      const decrement = typeof topicUnread === 'number' ? topicUnread : markedRead
      return typeof decrement === 'number'
        ? { ...chat, unread: Math.max(0, (chat.unread ?? 0) - decrement) }
        : chat
    }),
  )

  qc.setQueryData<Chat>(['chat', chatId], (chat) => {
    if (!chat) return chat
    if (allTopics) return { ...chat, unread: 0 }
    if (!topicId) return topicBackedChat ? chat : { ...chat, unread: 0 }
    const decrement = typeof topicUnread === 'number' ? topicUnread : markedRead
    return typeof decrement === 'number'
      ? { ...chat, unread: Math.max(0, (chat.unread ?? 0) - decrement) }
      : chat
  })

  if (topics) {
    qc.setQueryData<Topic[]>(['topics', chatId], (cur) =>
      (cur ?? []).map((topic) => (
        allTopics || topic.id === topicId ? { ...topic, unread: 0 } : topic
      )),
    )
  }

  qc.setQueriesData<Message[]>({ queryKey: ['messages', chatId] }, (cur) =>
    cur?.map((message) => {
      if (!message.unread) return message
      if (allTopics) return { ...message, unread: false }
      if (topicId) {
        return message.topic_id === topicId ? { ...message, unread: false } : message
      }
      if (topicBackedChat && (message.topic_id ?? null) !== null) return message
      return { ...message, unread: false }
    }),
  )
}

export function applyReadReceiptToClient(
  qc: QueryClient,
  chatId: string,
  patch: ReadStatePatch = {},
  versions?: MessageScopeVersions | null,
  options: { reconcileMessages?: boolean } = {},
): void {
  const normalizedPatch = {
    topicId: patch.topicId ?? null,
    allTopics: patch.allTopics === true,
    markedRead: patch.markedRead,
  }
  rememberReadReceiptOverlay(chatId, normalizedPatch, versions)
  applyReadStateToQueryCache(qc, chatId, normalizedPatch)
  void markLocalReadState(chatId, normalizedPatch)
  if (options.reconcileMessages && !normalizedPatch.allTopics) {
    reconcileMessageReadModelAfterRead(qc, chatId, normalizedPatch.topicId)
  }
}

function messageIdentityKeys(message: Message): string[] {
  return [
    message.id,
    message.client_id,
    message.external_message_id,
  ].filter((value): value is string => typeof value === 'string' && value.length > 0)
}

function isLocalPendingMessage(message: Message): boolean {
  return (
    message.direction === 'inbound' &&
    (message.status === 'queued' || message.status === 'dispatched') &&
    messageIdentityKeys(message).length > 0
  )
}

function mergeServerTruthWithLocalPending(
  serverMessages: Message[],
  current: Message[] | undefined,
): Message[] {
  if (!current || current.length === 0) return mergeVisibleMessages(serverMessages)
  const firstServerTs = serverMessages[0]?.ts ?? Number.NEGATIVE_INFINITY
  const serverKeys = new Set(serverMessages.flatMap(messageIdentityKeys))
  const pending = current.filter((message) => (
    isLocalPendingMessage(message) &&
    message.ts >= firstServerTs &&
    !messageIdentityKeys(message).some((key) => serverKeys.has(key))
  ))
  return mergeVisibleMessages(serverMessages, pending)
}

function mergeServerRefreshWithCurrent(
  serverMessages: Message[],
  current: Message[] | undefined,
  refreshContainsCompleteScope: boolean,
): Message[] {
  if (refreshContainsCompleteScope) {
    return mergeServerTruthWithLocalPending(serverMessages, current)
  }
  return mergeVisibleMessages(current, serverMessages)
}

function mergeLatestWindowRefresh(
  chatId: string,
  topicId: string | null,
  serverMessages: Message[],
  current: Message[] | undefined,
): Message[] {
  if (scopeHasExpandedMessageReadModel(chatId, topicId) && current && current.length > 0) {
    return mergeVisibleMessages(current, serverMessages)
  }
  return mergeServerTruthWithLocalPending(serverMessages, current)
}

function latestInboundIndex(messages: Message[]): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (messageIsMarkAuthoredInbound(message)) return index
  }
  return -1
}

function thirdLatestInboundContextIndex(messages: Message[]): number {
  let seen = 0
  let earliestInbound = -1
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (!messageIsMarkAuthoredInbound(message)) continue
    earliestInbound = index
    seen += 1
    if (seen === 3) return index
  }
  return earliestInbound
}

function normalOpenMessageWindow(
  messages: Message[],
  limit = MESSAGES_INITIAL_FETCH_LIMIT,
): Message[] {
  const inboundIndex = latestInboundIndex(messages)
  if (inboundIndex >= 0) {
    const rowsFromLatest = messages.length - inboundIndex
    if (rowsFromLatest >= limit) return messages.slice(-limit)
    const contextIndex = thirdLatestInboundContextIndex(messages)
    const remainingBeforeLatest = limit - rowsFromLatest
    const startIndex = Math.max(0, contextIndex, inboundIndex - remainingBeforeLatest)
    return messages.slice(startIndex)
  }
  return messages.length > limit ? messages.slice(-limit) : messages
}

function chatReadWarmVersion(chat: Chat): string {
  return [
    chat.last_activity_at ?? 0,
    chat.last_agent_reply_at ?? 0,
    chat.last_message_direction ?? '',
    chat.last_message_body ?? '',
    chat.unread ?? 0,
    chat.queue_depth ?? 0,
    chat.active_work_depth ?? 0,
    chat.message_scope_version ?? '',
  ].join('|')
}

function expectedMessageScopeVersion(
  qc: QueryClient,
  chatId: string,
  topicId: string | null,
): number | null {
  if (topicId) {
    const topics =
      qc.getQueryData<Topic[]>(['topics', chatId]) ??
      getSnapshotTopics(chatId) ??
      []
    return finiteScopeVersion(
      topics.find((topic) => topic.id === topicId)?.message_scope_version,
    )
  }

  const chat =
    qc.getQueryData<Chat>(['chat', chatId]) ??
    getSnapshotChat(chatId) ??
    qc.getQueryData<Chat[]>(['chats'])?.find((candidate) => candidate.id === chatId) ??
    getSnapshotChats()?.find((candidate) => candidate.id === chatId) ??
    null
  return finiteScopeVersion(chat?.message_scope_version)
}

export function preloadMessageReadModel(
  qc: QueryClient,
  chatId: string,
  topicId: string | null,
  limit = MESSAGE_TAIL_PRELOAD_LIMIT,
  options: { latestWindowOnly?: boolean } = { latestWindowOnly: true },
): void {
  if (!canTryNetwork()) return
  const latestWindowOnly = options.latestWindowOnly !== false
  const scopeKey = `${messageScopeKey(chatId, topicId)}:${limit}:${latestWindowOnly ? 'latest' : 'merge'}`
  if (_messagePreloadPending.has(scopeKey)) return
  const refreshContainsCompleteScope = !latestWindowOnly && limit >= MESSAGES_FETCH_LIMIT

  const task = (async () => {
    try {
      const serverMessages = normalizeUnreadAfterReadReceipt(
        chatId,
        topicId,
        await api<Message[]>(messagesPath(
          chatId,
          topicId,
          null,
          limit,
          latestWindowOnly ? 'latest_inbound' : null,
        )),
      )
      if (latestWindowOnly) {
        await saveLatestWindowReadModel(chatId, topicId, serverMessages, limit)
      } else {
        await saveLocalMessages(chatId, topicId, serverMessages, {
          merge: limit < MESSAGES_FETCH_LIMIT,
        })
      }
      const withOutbox = await mergeOutboxMessages(serverMessages, chatId, topicId)
      qc.setQueryData<Message[]>(
        messagesKey(chatId, topicId),
        (current) => (
          latestWindowOnly
            ? mergeLatestWindowRefresh(chatId, topicId, withOutbox, current)
            : mergeServerRefreshWithCurrent(
                withOutbox,
                current,
                refreshContainsCompleteScope,
              )
        ),
      )
    } catch {
      // Preload is opportunistic; the next chat-list poll or WS event retries.
    } finally {
      _messagePreloadPending.delete(scopeKey)
    }
  })()

  _messagePreloadPending.set(scopeKey, task)
}

export function refreshMessageReadModel(
  qc: QueryClient,
  chatId: string | undefined | null,
  topicId: string | null | undefined,
): void {
  if (!chatId) return
  const scopedTopicId = topicId ?? null
  if (scopeHasExpandedMessageReadModel(chatId, scopedTopicId)) {
    preloadMessageReadModel(qc, chatId, scopedTopicId, MESSAGE_TAIL_PRELOAD_LIMIT, {
      latestWindowOnly: true,
    })
    return
  }
  qc.invalidateQueries({ queryKey: messagesKey(chatId, scopedTopicId), exact: true })
}

export function refreshRetainedMessageReadModels(qc: QueryClient): void {
  for (const scope of retainedOpenMessageScopes()) {
    preloadMessageReadModel(qc, scope.chatId, scope.topicId, MESSAGE_TAIL_PRELOAD_LIMIT, {
      latestWindowOnly: true,
    })
  }
}

export async function expandMessageReadModel(
  qc: QueryClient,
  chatId: string,
  topicId: string | null,
  beforeMessageId: string,
  view?: { isCurrent: () => boolean; beforeMerge: () => void; requireCompleteArchive?: boolean },
): Promise<Message[]> {
  const mergeOlderPage = async (olderPage: Message[]): Promise<Message[]> => {
    const current = qc.getQueryData<Message[]>(messagesKey(chatId, topicId))
    if (view && !view.isCurrent()) return current ?? []
    if (olderPage.length === 0) {
      const exhausted = (current ?? []).map((message, index) => (
        index === 0
          ? {
              ...message,
              has_older_messages: false,
              history_scope_key: messageHistoryScopeKey(chatId, topicId),
            }
          : message
      ))
      view?.beforeMerge()
      qc.setQueryData<Message[]>(messagesKey(chatId, topicId), exhausted)
      return exhausted
    }
    const withOutbox = await mergeOutboxMessages(olderPage, chatId, topicId)
    if (view && !view.isCurrent()) return current ?? []
    const merged = mergeVisibleMessages(withOutbox, current)
    markExpandedMessageReadModel(chatId, topicId)
    view?.beforeMerge()
    qc.setQueryData<Message[]>(messagesKey(chatId, topicId), merged)
    return merged
  }

  const fetchLivePage = async (): Promise<Message[]> => normalizeUnreadAfterReadReceipt(
    chatId,
    topicId,
    await api<Message[]>(messagesPath(
      chatId,
      topicId,
      null,
      MESSAGES_OLDER_PAGE_LIMIT,
      null,
      beforeMessageId,
    )),
  )

  // Load older is a user gesture, so never make a synced page wait for the
  // network. Reveal the complete device archive first, then reconcile that
  // exact page in the background so server truth still wins when online.
  const archivedPage = await loadOlderArchivedMessages(
    chatId,
    topicId,
    beforeMessageId,
    MESSAGES_OLDER_PAGE_LIMIT,
  )
  // A partial archive can contain an older session with gaps in the current
  // one. Use a contiguous live page before treating that as a reset boundary.
  if (view?.requireCompleteArchive && !archivedPage.archiveComplete && canTryNetwork()) {
    return mergeOlderPage(await fetchLivePage())
  }
  if (archivedPage.messages.length > 0) {
    const expanded = await mergeOlderPage(normalizeUnreadAfterReadReceipt(
      chatId,
      topicId,
      archivedPage.messages,
    ))
    if (canTryNetwork()) {
      void fetchLivePage()
        .then((livePage) => mergeOlderPage(livePage))
        .catch(() => { /* The already-rendered device archive remains usable. */ })
    }
    return expanded
  }

  return mergeOlderPage(await fetchLivePage())
}

function reconcileMessageReadModelAfterRead(
  qc: QueryClient,
  chatId: string,
  topicId: string | null,
): void {
  if (!canTryNetwork()) return
  void (async () => {
    try {
      const serverMessages = normalizeUnreadAfterReadReceipt(
        chatId,
        topicId,
        await api<Message[]>(messagesPath(
          chatId,
          topicId,
          null,
          MESSAGE_TAIL_PRELOAD_LIMIT,
          'latest_inbound',
        )),
      )
      await saveLatestWindowReadModel(chatId, topicId, serverMessages)
      const withOutbox = await mergeOutboxMessages(serverMessages, chatId, topicId)
      qc.setQueryData<Message[]>(
        messagesKey(chatId, topicId),
        (current) => mergeLatestWindowRefresh(chatId, topicId, withOutbox, current),
      )
    } catch {
      // The read overlay still prevents older in-flight fetches from reviving
      // unread flags; normal query refetches remain the retry path.
    }
  })()
}

function preloadAllChatMessageTails(qc: QueryClient): void {
  if (!canTryNetwork()) return
  if (readWarmersSuspended()) return
  const now = Date.now()
  if (_allChatTailWarmPending || now - _allChatTailWarmAt < ALL_CHAT_TAIL_WARM_STALE_MS) return

  _allChatTailWarmPending = (async () => {
    try {
      const scopes = await api<MessageTailScope[]>(
        `/api/chats/message-tails?limit=${MESSAGE_TAIL_PRELOAD_LIMIT}`,
      )
      await Promise.all(scopes.map(async (scope) => {
        const messages = normalizeUnreadAfterReadReceipt(scope.chat_id, scope.topic_id, scope.messages)
        await saveLatestWindowReadModel(scope.chat_id, scope.topic_id, messages)
        const withOutbox = await mergeOutboxMessages(messages, scope.chat_id, scope.topic_id)
        qc.setQueryData<Message[]>(
          messagesKey(scope.chat_id, scope.topic_id),
          (current) => mergeLatestWindowRefresh(scope.chat_id, scope.topic_id, withOutbox, current),
        )
      }))
      _allChatTailWarmAt = Date.now()
    } catch {
      // The per-chat warmers and normal chat-open fetches remain the retry path.
    } finally {
      _allChatTailWarmPending = null
    }
  })()
}

function queueReadWarmTask(task: () => Promise<void>, delayMs: number): void {
  if (typeof window === 'undefined') return
  window.setTimeout(() => {
    if (readWarmersSuspended()) {
      queueReadWarmTask(task, readWarmResumeDelay())
      return
    }
    _readWarmQueue.push(task)
    drainReadWarmQueue()
  }, delayMs)
}

function drainReadWarmQueue(): void {
  if (readWarmersSuspended()) return
  while (_readWarmActive < READ_WARM_MAX_CONCURRENT && _readWarmQueue.length > 0) {
    const task = _readWarmQueue.shift()!
    _readWarmActive += 1
    void task()
      .catch(() => {})
      .finally(() => {
        _readWarmActive -= 1
        drainReadWarmQueue()
      })
  }
}

function queueTopicWarmTask(task: () => Promise<void>, delayMs: number): void {
  if (typeof window === 'undefined') return
  window.setTimeout(() => {
    if (readWarmersSuspended()) {
      queueTopicWarmTask(task, readWarmResumeDelay())
      return
    }
    _topicWarmChain = _topicWarmChain.then(task).catch(() => {})
  }, delayMs)
}

function scheduleTopicListWarm(qc: QueryClient, chats: Chat[]): void {
  if (!canTryNetwork() || typeof window === 'undefined') return
  if (readWarmersSuspended()) return
  const now = Date.now()
  selectTopicListWarmTargets(chats)
    .forEach((chat, index) => {
      const lastWarm = _topicListWarmAt.get(chat.id) ?? 0
      if (_topicListWarmPending.has(chat.id) || now - lastWarm < TOPIC_LIST_WARM_STALE_MS) return
      _topicListWarmPending.add(chat.id)
      queueTopicWarmTask(async () => {
        try {
          const topics = await api<Topic[]>(topicsPath(chat.id))
          await saveLocalTopics(chat.id, topics)
          qc.setQueryData(['topics', chat.id], topics)
          preloadRecentTopicMessages(qc, chat.id, topics)
          _topicListWarmAt.set(chat.id, Date.now())
        } finally {
          _topicListWarmPending.delete(chat.id)
        }
      }, TOPIC_LIST_WARM_INITIAL_DELAY_MS + index * TOPIC_LIST_WARM_STEP_MS)
    })
}

function scheduleRecentChatReadWarm(qc: QueryClient, chats: Chat[]): void {
  if (!canTryNetwork() || typeof window === 'undefined') return
  if (readWarmersSuspended()) return
  const now = Date.now()
  const recent = chats
    .filter((chat) => !chat.archived && shouldWarmIndividualChat(chat))
    .sort((a, b) =>
      (chatReadWarmPriority(a) - chatReadWarmPriority(b)) ||
      (chatActivity(b) - chatActivity(a)),
    )

  recent.forEach((chat, index) => {
    const lastWarm = _readWarmAt.get(chat.id) ?? 0
    const version = chatReadWarmVersion(chat)
    const versionUnchanged = _readWarmVersions.get(chat.id) === version
    if (
      _readWarmPending.has(chat.id) ||
      (versionUnchanged && now - lastWarm < READ_WARM_STALE_MS)
    ) return
    _readWarmPending.add(chat.id)
    queueReadWarmTask(async () => {
      try {
        await warmChatReadModel(qc, chat, MESSAGE_TAIL_PRELOAD_LIMIT)
        _readWarmAt.set(chat.id, Date.now())
        _readWarmVersions.set(chat.id, version)
      } finally {
        _readWarmPending.delete(chat.id)
      }
    }, RECENT_READ_WARM_INITIAL_DELAY_MS + index * RECENT_READ_WARM_STEP_MS)
  })
}

async function warmChatReadModel(
  qc: QueryClient,
  chatOrId: Chat | string,
  limit = MESSAGES_FETCH_LIMIT,
): Promise<void> {
  if (!canTryNetwork()) return
  const chatId = typeof chatOrId === 'string' ? chatOrId : chatOrId.id
  try {
    const chat = typeof chatOrId === 'string'
      ? await api<Chat>(`/api/chats/${chatId}`)
      : chatOrId
    await saveLocalChat(chat)
    qc.setQueryData(['chat', chatId], chat)
  } catch {
    return
  }

  if (typeof chatOrId !== 'string' && chatOrId.type === 'auto-spawn') {
    if (chatOrId.single_unread_topic_id) {
      preloadMessageReadModel(qc, chatId, chatOrId.single_unread_topic_id, limit, {
        latestWindowOnly: true,
      })
    }
    return
  }

  try {
    const messages = normalizeUnreadAfterReadReceipt(
      chatId,
      null,
      await api<Message[]>(messagesPath(chatId, null, null, limit, 'latest_inbound')),
    )
    await saveLatestWindowReadModel(chatId, null, messages, limit)
    const withOutbox = await mergeOutboxMessages(messages, chatId, null)
    qc.setQueryData<Message[]>(
      messagesKey(chatId, null),
      (current) => mergeLatestWindowRefresh(chatId, null, withOutbox, current),
    )
  } catch {
    // Chat detail is still useful offline even when messages fail.
  }

  // Group topic lists/tails are warmed by scheduleTopicListWarm. Manual chats
  // only need their message tail here.
}

function preloadRecentTopicMessages(
  qc: QueryClient,
  chatId: string,
  topics: Topic[],
): void {
  selectTopicReadWarmTargets(topics, RECENT_TOPIC_READ_WARM_LIMIT)
    .forEach((topic) => preloadMessageReadModel(qc, chatId, topic.id, MESSAGE_TAIL_PRELOAD_LIMIT, {
      latestWindowOnly: true,
    }))
}

type UseMessagesOptions = {
  enabled?: boolean
  aroundMessageId?: string | null
  notificationOpen?: boolean
  notificationTargetMessageId?: string | null
  latestWindowOnly?: boolean
}

type NotificationOpenBundle = {
  chat_id?: string | null
  topic_id?: string | null
  message_id?: string | null
  chat?: Chat | null
  topics?: Topic[]
  messages?: Message[]
}

export function useMessages(
  chatId: string | undefined,
  topicId?: string | null,
  options: UseMessagesOptions = {},
) {
  const qc = useQueryClient()
  const aroundMessageId = options.aroundMessageId ?? null
  const enabled = options.enabled ?? true
  const notificationOpen = options.notificationOpen === true
  const notificationTargetMessageId = options.notificationTargetMessageId ?? null
  const latestWindowOnly = options.latestWindowOnly === true && !aroundMessageId && !notificationOpen
  const scopedTopicId = topicId ?? null
  const result = useQuery({
    // A notification open is a faster transport for the same scope, not a
    // separate scope: it shares the canonical messages key so Load older, the
    // WebSocket tail merges, and read-receipt reconciliation all write to the
    // array the view is actually rendering.
    queryKey: aroundMessageId
      ? ['messages', chatId, topicId ?? null, 'around', aroundMessageId] as const
      : messagesKey(chatId, topicId),
    queryFn: async () => {
      const exactLocalNotification = async (): Promise<Message[] | null> => {
        if (!notificationTargetMessageId) return null
        const cached = await loadRenderableLocalMessages(chatId!, scopedTopicId)
        const exact = cached?.some((message) => message.id === notificationTargetMessageId)
          ? cached
          : await loadArchivedMessageWindow(chatId!, scopedTopicId, notificationTargetMessageId)
        return exact ? mergeOutboxMessages(exact, chatId!, scopedTopicId) : null
      }
      const preserveOpenReadModel = (
        latestWindowOnly &&
        scopeHasExpandedMessageReadModel(chatId!, scopedTopicId)
      )
      if (!aroundMessageId) {
        if (!canTryNetwork()) {
          if (notificationTargetMessageId) {
            const exact = await exactLocalNotification()
            if (exact) return exact
            throw new Error('Offline. Notification message is not saved on this device.')
          }
          const currentMessages = preserveOpenReadModel
            ? qc.getQueryData<Message[]>(messagesKey(chatId!, scopedTopicId))
            : null
          if (currentMessages && currentMessages.length > 0) {
            return mergeOutboxMessages(currentMessages, chatId!, scopedTopicId)
          }
          const freshCached = await loadRenderableLocalMessages(chatId!, scopedTopicId)
          if (freshCached) {
            return mergeOutboxMessages(
              latestWindowOnly
                ? latestWindowReadModel(chatId!, scopedTopicId, freshCached)
                : freshCached,
              chatId!,
              scopedTopicId,
            )
          }
          throw new Error('Offline. Latest messages unavailable.')
        }
        if (!notificationOpen && !preserveOpenReadModel) {
          const expectedVersion = expectedMessageScopeVersion(qc, chatId!, scopedTopicId)
          if (expectedVersion !== null) {
            const currentLocal = await loadFreshLocalMessages(
              chatId!,
              scopedTopicId,
              { expectedVersion },
            )
            if (currentLocal) {
              const normalizedLocal = normalizeUnreadAfterReadReceipt(
                chatId!,
                scopedTopicId,
                currentLocal,
              )
              return mergeOutboxMessages(
                latestWindowOnly
                  ? latestWindowReadModel(chatId!, scopedTopicId, normalizedLocal)
                  : normalizedLocal,
                chatId!,
                scopedTopicId,
              )
            }
          }
        }
      }
      if (notificationOpen && !aroundMessageId) {
        let bundle: NotificationOpenBundle
        try {
          bundle = await api<NotificationOpenBundle>(
            notificationOpenBundlePath(chatId!, scopedTopicId, notificationTargetMessageId),
          )
        } catch (err) {
          // Android can retain navigator.onLine=true after its private route
          // disappears. A failed network probe must still open the exact
          // notification from the durable device archive.
          const unavailable = err instanceof TypeError ||
            (err instanceof Error && /^Server error \(5\d\d\)/.test(err.message))
          if (unavailable) {
            const exact = await exactLocalNotification()
            if (exact) return exact
          }
          throw err
        }
        const bundleTopicId = bundle.topic_id || scopedTopicId
        const serverMessages = Array.isArray(bundle.messages) ? bundle.messages : []
        if (bundle.chat) {
          await saveLocalChat(bundle.chat)
          qc.setQueryData<Chat>(['chat', chatId!], bundle.chat)
        }
        const topicUpdates = bundle.topics
        if (Array.isArray(topicUpdates)) {
          await saveLocalTopics(chatId!, topicUpdates, { merge: true })
          const savedTopics = await loadLocalTopics(chatId!)
          qc.setQueryData<Topic[]>(['topics', chatId!], (current) => (
            mergeTopicUpdates(mergeTopicUpdates(savedTopics ?? [], current ?? []), topicUpdates)
          ))
          void qc.invalidateQueries({ queryKey: ['topics', chatId!], exact: true })
        }
        const normalizedMessages = normalizeUnreadAfterReadReceipt(chatId!, bundleTopicId, serverMessages)
        await saveLocalMessages(chatId!, bundleTopicId, normalizedMessages, { merge: true })
        const withOutbox = await mergeOutboxMessages(normalizedMessages, chatId!, bundleTopicId)
        const merged = mergeServerRefreshWithCurrent(
          withOutbox,
          qc.getQueryData<Message[]>(messagesKey(chatId!, bundleTopicId)),
          false,
        )
        qc.setQueryData<Message[]>(messagesKey(chatId!, bundleTopicId), merged)
        return merged
      }
      const serverMessages = normalizeUnreadAfterReadReceipt(
        chatId!,
        topicId ?? null,
        await api<Message[]>(messagesPath(
          chatId!,
          topicId,
          aroundMessageId,
          aroundMessageId ? MESSAGES_FETCH_LIMIT : MESSAGES_INITIAL_FETCH_LIMIT,
          latestWindowOnly ? 'latest_inbound' : null,
        )),
      )
      if (aroundMessageId) {
        await saveLocalMessages(chatId!, topicId ?? null, serverMessages, { merge: true })
        return serverMessages
      }
      if (latestWindowOnly) {
        await saveLatestWindowReadModel(chatId!, topicId ?? null, serverMessages)
      } else {
        await saveLocalMessages(chatId!, topicId ?? null, serverMessages)
      }
      const withOutbox = await mergeOutboxMessages(serverMessages, chatId!, topicId ?? null)
      const current = qc.getQueryData<Message[]>(messagesKey(chatId!, topicId ?? null))
      return latestWindowOnly
        ? mergeLatestWindowRefresh(chatId!, topicId ?? null, withOutbox, current)
        : mergeServerRefreshWithCurrent(withOutbox, current, false)
    },
    enabled: !!chatId && enabled,
    // Synchronous render path on notification tap. The IDB snapshot hydrated
    // at boot (and topped up by the SW push prefetch) lets useMessages return
    // data in the FIRST render, so the gray skeleton bubbles never flash. The
    // async queryFn still runs in the background to merge any extra rows the
    // snapshot may be missing.
    placeholderData: () => {
      if (!chatId) return undefined
      const snap = getRenderableSnapshotMessages(chatId, scopedTopicId)
      const exactTarget = aroundMessageId || notificationTargetMessageId
      if (exactTarget && !snap?.some((message) => message.id === exactTarget)) {
        return undefined
      }
      if (!snap || snap.length === 0) return undefined
      return latestWindowOnly ? latestWindowReadModel(chatId, scopedTopicId, snap) : snap
    },
    // Cache lives forever during the session. WS/journal events, mount,
    // reconnect, foreground, send/final-reply paths reconcile truth through
    // targeted latest-tail merges. A blind interval is intentionally disabled:
    // polling must never become a second writer that rewrites the transcript
    // Mark is reading.
    staleTime: Infinity,
    gcTime: Infinity,           // never GC while app is open
    refetchOnMount: 'always',
    refetchInterval: false,
    refetchIntervalInBackground: false,
    networkMode: 'always',
  })

  return result
}

function outboxBody(item: OutboxItem): string {
  const text = item.body.trim()
  if (text) return text
  const count = item.attachments?.length ?? 0
  if (count === 0) return ''
  const imageCount = item.attachments?.filter((att) => att.kind === 'image').length ?? 0
  if (imageCount === count) return count === 1 ? 'Attached 1 image.' : `Attached ${count} images.`
  return count === 1 ? 'Attached 1 file.' : `Attached ${count} files.`
}

async function mergeOutboxMessages(
  serverMessages: Message[],
  chatId: string,
  topicId: string | null,
): Promise<Message[]> {
  const outbox = await listOutbox(chatId)
  if (outbox.length === 0) return serverMessages
  const firstServerTs = serverMessages[0]?.ts ?? Number.NEGATIVE_INFINITY
  const serverIds = new Set(serverMessages.flatMap((m) => [
    m.id,
    m.client_id,
    m.external_message_id,
  ]).filter(Boolean))
  const pending = outbox
    .filter((item) => (item.topic_id ?? null) === topicId)
    .filter((item) => !serverIds.has(item.id))
    .filter((item) => Math.floor(item.created_at / 1000) >= firstServerTs)
    .map<Message>((item) => ({
      id: item.id,
      client_id: item.id,
      topic_id: item.topic_id,
      external_message_id: item.id,
      direction: 'inbound',
      user: 'mark',
      ts: Math.floor(item.created_at / 1000),
      body: outboxBody(item),
      status: item.last_error || item.attempts > 0 ? 'error' : 'queued',
      queue_position: null,
      force_dispatch: false,
      reply_to_message_id: item.reply_to_message_id ?? null,
      unread: false,
      deleted: false,
      outbox_pending: true,
      outbox_error: item.last_error ?? null,
      attachments: (item.attachments || []).map((att) => ({
        id: att.id,
        kind: att.kind,
        filename: att.filename,
        mime_type: att.mime_type,
        size_bytes: att.size_bytes,
        url: outboxAttachmentPreviewUrl(item, att) || `/api/attachments/${att.id}/file`,
      })),
    }))
  if (pending.length === 0) return mergeVisibleMessages(serverMessages)
  return mergeVisibleMessages(serverMessages, pending)
}

/**
 * Hook returning a `prefetchChat(chatId)` that warms BOTH the chat-detail
 * cache and the messages cache. Call this on touchstart of a chat row in
 * the chat list — by the time React Router has navigated, the messages
 * are already in the cache and MessageList renders without a spinner.
 */
export function usePrefetchChat() {
  const qc = useQueryClient()
  return useCallback((
    chatId: string,
    topicId?: string | null,
    options: { messages?: boolean; topics?: boolean; chat?: boolean; topicMessages?: number } = {},
  ) => {
    if (!chatId) return
    if (readWarmersSuspended()) return
    const shouldPrefetchMessages = options.messages ?? true
    const shouldPrefetchTopics = options.topics ?? true
    const shouldPrefetchChat = options.chat ?? true
    const topicMessageWarmLimit = Math.max(0, Math.floor(options.topicMessages ?? 0))
    const warmTopicMessageScopes = (topics: Topic[] | null | undefined): void => {
      if (topicMessageWarmLimit <= 0 || !topics || topics.length === 0) return
      selectTopicReadWarmTargets(topics, topicMessageWarmLimit)
        .forEach((topic) => {
          const key = messagesKey(chatId, topic.id)
          void loadRenderableLocalMessages(chatId, topic.id).then((cached) => {
            if (!cached) return
            qc.setQueryData<Message[]>(key, latestWindowReadModel(chatId, topic.id, cached))
          }).catch(() => {})
          preloadMessageReadModel(qc, chatId, topic.id, MESSAGE_TAIL_PRELOAD_LIMIT, {
            latestWindowOnly: true,
          })
        })
    }
    if (shouldPrefetchTopics) {
      void loadLocalTopics(chatId).then((cached) => {
        if (cached && !qc.getQueryData(['topics', chatId])) {
          qc.setQueryData(['topics', chatId], cached)
        }
        warmTopicMessageScopes(cached)
      }).catch(() => {})
      void qc.prefetchQuery({
        queryKey: ['topics', chatId],
        queryFn: async () => {
          const topics = await api<Topic[]>(topicsPath(chatId))
          await saveLocalTopics(chatId, topics)
          warmTopicMessageScopes(topics)
          return topics
        },
        staleTime: 30_000,
        networkMode: 'always',
      })
    }
    if (shouldPrefetchMessages) {
      const scopedTopicId = topicId ?? null
      void loadRenderableLocalMessages(chatId, scopedTopicId).then((cached) => {
        if (!cached) return
        const key = messagesKey(chatId, scopedTopicId)
        qc.setQueryData<Message[]>(key, latestWindowReadModel(chatId, scopedTopicId, cached))
      }).catch(() => {})
      preloadMessageReadModel(qc, chatId, scopedTopicId, MESSAGE_TAIL_PRELOAD_LIMIT, {
        latestWindowOnly: true,
      })
    }
    if (shouldPrefetchChat) {
      void loadLocalChat(chatId).then((cached) => {
        if (cached && !qc.getQueryData(['chat', chatId])) {
          qc.setQueryData(['chat', chatId], cached)
        }
      }).catch(() => {})
      void qc.prefetchQuery({
        queryKey: ['chat', chatId],
        queryFn: async () => {
          const live = await api<Chat>(`/api/chats/${chatId}`)
          await saveLocalChat(live)
          return live
        },
        staleTime: 30_000,
        networkMode: 'always',
      })
    }
  }, [qc])
}

function clearUnreadForSentScope(
  qc: QueryClient,
  chatId: string,
  topicId: string | null,
): void {
  const topics = qc.getQueryData<Topic[]>(['topics', chatId])
  const topicUnread = topicId
    ? topics?.find((topic) => topic.id === topicId)?.unread
    : undefined
  const cachedChat = (
    qc.getQueryData<Chat>(['chat', chatId])
    ?? qc.getQueryData<Chat[]>(['chats'])?.find((chat) => chat.id === chatId)
  )
  const topicBackedChat = (topics?.length ?? 0) > 0 || cachedChat?.type === 'auto-spawn'

  qc.setQueryData<Chat[]>(['chats'], (cur) =>
    (cur ?? []).map((chat) => {
      if (chat.id !== chatId) return chat
      if (!topicId) return topicBackedChat ? chat : { ...chat, unread: 0 }
      if (typeof topicUnread !== 'number') return chat
      return { ...chat, unread: Math.max(0, (chat.unread ?? 0) - topicUnread) }
    }),
  )

  if (topicId) {
    qc.setQueryData<Topic[]>(['topics', chatId], (cur) =>
      cur?.map((topic) => (
        topic.id === topicId ? { ...topic, unread: 0 } : topic
      )),
    )
  }
}

type OptimisticSendContext = {
  previous?: Message[]
  previousTopic?: Message[] | null
  previousTopics?: Topic[] | null
  tempId: string
  topicId: string | null
}

export type StagedOutgoingMessage = {
  ready: Promise<void>
  rollback: () => void
}

function messageMatchesClientId(message: Message, clientId: string): boolean {
  return message.id === clientId ||
    message.client_id === clientId ||
    message.external_message_id === clientId
}

function cancelSendReadModels(
  qc: QueryClient,
  chatId: string,
  topicId: string | null,
): Promise<void> {
  const exactChatKey = messagesKey(chatId, null)
  const exactTopicKey = topicId ? messagesKey(chatId, topicId) : null
  return Promise.allSettled([
    qc.cancelQueries({ queryKey: exactChatKey }, { revert: false }),
    exactTopicKey
      ? qc.cancelQueries({ queryKey: exactTopicKey }, { revert: false })
      : Promise.resolve(),
    qc.cancelQueries({ queryKey: ['chats'] }, { revert: false }),
    qc.cancelQueries({ queryKey: ['topics', chatId] }, { revert: false }),
  ]).then((results) => {
    if (results.some((result) => result.status === 'rejected')) {
      console.warn('send read-model cancellation did not fully settle')
    }
  })
}

function optimisticMessage(body: SendMessageInput, tempId: string): Message {
  return {
    id: tempId,
    // The client id is also the stable Virtuoso key. It keeps one DOM row
    // mounted while the server id and delivery status replace local state.
    client_id: tempId,
    topic_id: body.topic_id ?? null,
    external_message_id: body.client_id ?? null,
    direction: 'inbound',
    user: 'mark',
    ts: Math.floor(Date.now() / 1000),
    body: body.text,
    status: 'queued',
    queue_position: null,
    force_dispatch: body.force ?? false,
    reply_to_message_id: body.reply_to_message_id ?? null,
    unread: false,
    deleted: false,
    // A client-id send is still only in the device outbox until POST returns.
    // MessageList renders this as Sending, never as a fake server Queue #1.
    outbox_pending: Boolean(body.client_id),
    attachments: (body.attachments || []).map((attachment) => ({
      id: attachment.id,
      kind: attachment.kind,
      filename: attachment.filename,
      mime_type: attachment.mime_type,
      size_bytes: attachment.size_bytes,
      url: attachment.preview_url || `/api/attachments/${attachment.id}/file`,
    })),
  }
}

function applyOptimisticSend(
  qc: QueryClient,
  chatId: string,
  body: SendMessageInput,
): OptimisticSendContext {
  const chatKey = messagesKey(chatId, null)
  const topicId = body.topic_id ?? null
  const topicKey = topicId ? messagesKey(chatId, topicId) : null
  const previous = qc.getQueryData<Message[]>(chatKey) || []
  const previousTopic = topicKey ? qc.getQueryData<Message[]>(topicKey) || [] : null
  const tempId = body.client_id || `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const message = optimisticMessage(body, tempId)
  const replaceSameClientMessage = (messages: Message[]) => [
    ...messages.filter((candidate) => !messageMatchesClientId(candidate, tempId)),
    message,
  ]

  qc.setQueryData<Message[]>(chatKey, replaceSameClientMessage(previous))
  if (topicKey && previousTopic) {
    qc.setQueryData<Message[]>(topicKey, replaceSameClientMessage(previousTopic))
  }
  return { previous, previousTopic, tempId, topicId }
}

function rollbackOptimisticSend(
  qc: QueryClient,
  chatId: string,
  context: OptimisticSendContext,
): void {
  const removeStagedMessage = (messages: Message[] | undefined) => messages?.filter(
    (message) => !messageMatchesClientId(message, context.tempId),
  )
  qc.setQueryData<Message[]>(messagesKey(chatId, null), removeStagedMessage)
  if (context.topicId) {
    qc.setQueryData<Message[]>(messagesKey(chatId, context.topicId), removeStagedMessage)
  }
}

/** Paint the outgoing bubble in the same tap task, before IndexedDB or network
 * work can delay visual acknowledgement. The composer still keeps its draft
 * until the outbox write is durable; rollback is only for that write failing. */
export function useStageOutgoingMessage(chatId: string | undefined) {
  const qc = useQueryClient()
  return useCallback((body: SendMessageInput): StagedOutgoingMessage => {
    if (!chatId) {
      return { ready: Promise.resolve(), rollback: () => {} }
    }
    // Cancellation starts synchronously and cannot revert the just-staged row.
    const ready = cancelSendReadModels(qc, chatId, body.topic_id ?? null)
    const context = applyOptimisticSend(qc, chatId, body)
    return {
      ready,
      rollback: () => rollbackOptimisticSend(qc, chatId, context),
    }
  }, [chatId, qc])
}

export function useSendMessage(chatId: string | undefined) {
  const qc = useQueryClient()
  type SendMessageResult = {
    ok: boolean
    message_id: string
    queued: boolean
    session_title?: string | null
    _outbox?: boolean
    outbox_sent?: number
  }
  return useMutation({
    // Composer normally pre-stages this bubble before its IndexedDB write.
    // Other send surfaces still get the same immediate optimistic path here.
    onMutate: async (body: SendMessageInput) => {
      if (!chatId) return
      const key = messagesKey(chatId, null)
      const topicKey = body.topic_id ? messagesKey(chatId, body.topic_id) : null
      const tempId = body.client_id || `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const alreadyStaged = [
        ...(qc.getQueryData<Message[]>(key) || []),
        ...(topicKey ? qc.getQueryData<Message[]>(topicKey) || [] : []),
      ].some((message) => messageMatchesClientId(message, tempId))
      const cancelPending = alreadyStaged
        ? Promise.resolve()
        : cancelSendReadModels(qc, chatId, body.topic_id ?? null)
      const optimisticContext = alreadyStaged
        ? { tempId, topicId: body.topic_id ?? null }
        : applyOptimisticSend(qc, chatId, body)
      clearUnreadForSentScope(qc, chatId, body.topic_id ?? null)
      // Optimistically mark the topic as queued so it immediately leaves the
      // "finished" filter the moment the user sends a message.
      const previousTopics = qc.getQueryData<Topic[]>(['topics', chatId])
      if (body.topic_id && previousTopics) {
        qc.setQueryData<Topic[]>(['topics', chatId], previousTopics.map((t) =>
          t.id === body.topic_id ? { ...t, status: 'queued' } : t
        ))
      }
      await cancelPending
      return { ...optimisticContext, previousTopics: previousTopics ?? null }
    },
    mutationFn: async (body: SendMessageInput) => {
      const allAttachments = body.attachments || []
      const uploadedAttachments = allAttachments
        .filter((att) => typeof att.local_path === 'string' && att.local_path.length > 0)
      if (body.client_id) {
        // Composer promoted this prompt into IndexedDB before reaching us.
        // The outbox is the only network-delivery owner for client-id sends;
        // bypassing it here lets first-send/reconnect/retry POST concurrently.
        const drained = await drainOutbox()
        const delivery = drained.deliveries.find((item) => item.client_id === body.client_id)
        if (delivery) {
          return {
            ...delivery.receipt,
            _outbox: false,
            outbox_sent: drained.sent,
          } satisfies SendMessageResult
        }
        return {
          ok: false,
          message_id: body.client_id,
          queued: false,
          _outbox: true,
          outbox_sent: drained.sent,
        } satisfies SendMessageResult
      }
      const payload = {
        ...body,
        client_id: body.client_id,
        attachments: uploadedAttachments.map((att) => ({
          id: att.id,
          kind: att.kind,
          filename: att.filename,
          mime_type: att.mime_type,
          size_bytes: att.size_bytes,
          local_path: att.local_path!,
        })),
      }
      try {
        const result = await api<SendMessageResult>(
          `/api/chats/${chatId}/messages`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          },
        )
        return result
      } catch (err) {
        if (chatId && err instanceof TypeError) {
          const id = await enqueueOutbox(
            chatId,
            body.text,
            body.force,
            body.topic_id ?? null,
            (body.attachments || []).map((att) => ({
              id: att.id,
              kind: att.kind,
              filename: att.filename,
              mime_type: att.mime_type,
              size_bytes: att.size_bytes,
              local_path: att.local_path,
              preview_url: att.preview_url,
            })),
            body.model_override ?? null,
            body.effort_override ?? null,
            undefined,
            body.reply_to_message_id ?? null,
          )
          return { ok: false, message_id: id, queued: false, _outbox: true } satisfies SendMessageResult
        }
        throw err
      }
    },
    onError: (_err, _vars, ctx) => {
      // Roll back the optimistic addition.
      if (ctx?.previous && chatId) {
        qc.setQueryData(messagesKey(chatId, null), ctx.previous)
        if (ctx.topicId) {
          qc.setQueryData(messagesKey(chatId, ctx.topicId), ctx.previousTopic || [])
        }
      }
      if (ctx?.previousTopics && chatId) {
        qc.setQueryData<Topic[]>(['topics', chatId], ctx.previousTopics)
      }
    },
    onSuccess: (data, _body, ctx) => {
      // Replace the optimistic temp message with the server's truth
      // INLINE in the cache. Was: ``onSettled`` invalidated the
      // ``['messages', chatId]`` query, which triggered a refetch
      // (~200ms-2s+ on a phone over Tailscale). During that refetch
      // window the optimistic temp was wiped and the new message
      // hadn't arrived yet — Mark's just-sent bubble would VANISH
      // for seconds before reappearing. Replacing inline keeps the
      // bubble on screen continuously: same body, just swap the temp
      // id for the real one.
      if (!chatId || !ctx?.tempId || !data?.message_id) return
      if (data.session_title) {
        qc.setQueryData<Chat>(['chat', chatId], (chat) =>
          chat ? { ...chat, session_title: data.session_title ?? null } : chat,
        )
        qc.setQueryData<Chat[]>(['chats'], (chats) =>
          chats?.map((chat) => (
            chat.id === chatId ? { ...chat, session_title: data.session_title ?? null } : chat
          )),
        )
      }
      const replaceTemp = (prev: Message[] | undefined) => {
        const list = prev || []
        const idx = list.findIndex((m) => m.id === ctx.tempId)
        if (idx < 0) return list
        const next = list.slice()
        // Preserve `client_id` across the swap. Without it MessageList's
        // Virtuoso key (`m.client_id ?? m.id`) would fall back to `m.id`
        // and the row would re-key from `temp-…` to the real uuid —
        // visible bubble unmount/remount, the residual flicker.
        const real = {
          ...list[idx]!,
          id: data.message_id,
          client_id: list[idx]!.client_id ?? ctx.tempId,
          status: data._outbox
            ? list[idx]!.status
            : data.queued
              ? list[idx]!.status
              : 'replied',
          queue_position: data.queued ? list[idx]!.queue_position : null,
          force_dispatch: data.queued ? list[idx]!.force_dispatch : false,
          outbox_pending: Boolean(data._outbox),
          outbox_error: data._outbox ? list[idx]!.outbox_error : null,
        }
        next[idx] = real
        return next
      }
      qc.setQueryData<Message[]>(messagesKey(chatId, null), replaceTemp)
      if (ctx.topicId) {
        qc.setQueryData<Message[]>(messagesKey(chatId, ctx.topicId), replaceTemp)
      }
      if (data._outbox && data.outbox_sent && data.outbox_sent > 0) {
        refreshMessageReadModel(qc, chatId, null)
        if (ctx.topicId) refreshMessageReadModel(qc, chatId, ctx.topicId)
      }
      if (!data._outbox && !data.queued && ctx.topicId) {
        qc.setQueryData<Topic[]>(['topics', chatId], (topics) =>
          topics?.map((topic) => (
            topic.id === ctx.topicId ? { ...topic, status: 'idle' } : topic
          )),
        )
      }
    },
    onSettled: () => {
      // Light-touch sync. Don't invalidate ['messages'] — we already
      // replaced the temp inline in onSuccess. Just refresh chats/topics
      // so the list-row preview ts/last-activity catches up.
      qc.invalidateQueries({ queryKey: ['chats'] })
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
    },
  })
}

// ── Phase 2 hooks ─────────────────────────────────────────────────────────

export type Skill = {
  name: string
  command: string
  description: string
  sources: string[]
  enabled_for_lane?: boolean
}

export type SkillDoc = {
  name: string
  command: string
  description: string
  sources: string[]
  path: string
  summary: string[]
  document: string
}

export type SkillIndexLocation = {
  root: string
  scope: string
  type: 'skill' | 'slash_command'
  path: string
  content_path: string
  cursor_alias: boolean
  symlink: boolean
}

export type SkillIndexItem = {
  name: string
  command: string
  description: string
  surface_types: Array<'skill' | 'slash_command'>
  scopes: string[]
  locations: SkillIndexLocation[]
  summary: string[]
  usage_notes: string[]
  created_at: number | null
  created_commit: string | null
  creation_trigger: string | null
  last_edited_at: number | null
  last_edited_commit: string | null
  last_edited_summary: string | null
  usage_count: number
  last_used_at: number | null
}

export type SkillIndexResponse = {
  updated_at: number
  watched_roots: string[]
  roots: Array<{
    label: string
    path: string
    type: 'skill' | 'slash_command'
    scope: string
    cursor_alias: boolean
    exists: boolean
  }>
  stats: {
    total: number
    slash_commands: number
    skills: number
    with_usage: number
    cursor_alias_locations: number
  }
  items: SkillIndexItem[]
}

const SKILLS_QUERY_KEY = ['skills', 'all', 'v2'] as const
const SKILL_INDEX_QUERY_KEY = ['skill-index'] as const
const SKILLS_LOCAL_CACHE_KEY = 'agentchat:slash-skills-cache:v2'
const SKILLS_LIMIT = 500

type CachedSkillsPayload = {
  updatedAt: number
  skills: Skill[]
}

function readCachedSkills(): CachedSkillsPayload | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(SKILLS_LOCAL_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CachedSkillsPayload>
    if (!Array.isArray(parsed.skills) || typeof parsed.updatedAt !== 'number') return null
    return {
      updatedAt: parsed.updatedAt,
      skills: parsed.skills.filter((s): s is Skill =>
        !!s &&
        typeof s.name === 'string' &&
        typeof s.command === 'string' &&
        typeof s.description === 'string' &&
        Array.isArray(s.sources),
      ),
    }
  } catch {
    return null
  }
}

function writeCachedSkills(skills: Skill[]): void {
  if (typeof window === 'undefined') return
  try {
    const payload: CachedSkillsPayload = { updatedAt: Date.now(), skills }
    window.localStorage.setItem(SKILLS_LOCAL_CACHE_KEY, JSON.stringify(payload))
  } catch {
    // Storage pressure or private mode should not block the slash picker.
  }
}

export function clearCachedSkills(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(SKILLS_LOCAL_CACHE_KEY)
  } catch {
    // Ignore storage errors; the next network refresh will still correct data.
  }
}

function normaliseSkillLookup(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function filterSkills(skills: Skill[], query: string, limit: number): Skill[] {
  const q = query.trim().toLowerCase()
  if (!q) return skills.slice(0, limit)
  const qNorm = normaliseSkillLookup(q)
  const scored: Array<{ score: number; skill: Skill }> = []
  for (const skill of skills) {
    const name = skill.name.toLowerCase()
    const command = skill.command.toLowerCase()
    const description = skill.description.toLowerCase()
    const nameNorm = normaliseSkillLookup(skill.name)
    const commandNorm = normaliseSkillLookup(skill.command)
    let score = 0
    if (command.includes(q)) score += 3
    if (name.includes(q)) score += 2
    if (description.includes(q)) score += 1
    if (qNorm && nameNorm.startsWith(qNorm)) score += 5
    if (qNorm && nameNorm.includes(qNorm)) score += 2
    if (qNorm && commandNorm.includes(qNorm)) score += 1
    if (score > 0) scored.push({ score, skill })
  }
  scored.sort((a, b) => b.score - a.score || a.skill.name.localeCompare(b.skill.name))
  return scored.slice(0, limit).map(({ skill }) => skill)
}

async function fetchAllSkills(): Promise<Skill[]> {
  const skills = await api<Skill[]>(`/api/skills?limit=${SKILLS_LIMIT}`)
  writeCachedSkills(skills)
  return skills
}

export function warmSkills(qc: QueryClient): void {
  void qc.prefetchQuery({
    queryKey: SKILLS_QUERY_KEY,
    queryFn: fetchAllSkills,
    staleTime: 10 * 60 * 1000,
    gcTime: 24 * 60 * 60_000,
  })
}

export function warmSkillIndex(qc: QueryClient): void {
  void qc.prefetchQuery({
    queryKey: SKILL_INDEX_QUERY_KEY,
    queryFn: () => api<SkillIndexResponse>('/api/skills/index'),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60_000,
  })
}

export type SearchHit = {
  message_id: string
  chat_id: string
  topic_id: string | null
  direction: string
  user: string | null
  ts: number
  body: string
  chat_display_name: string | null
  platform_id: string
  agent: Agent
}

export type SearchAuthorFilter = 'all' | 'me' | 'agentchat'

export type SearchFilters = {
  author?: SearchAuthorFilter
  fromTs?: number | null
  toTs?: number | null
}

export type SearchOptions = {
  enabled?: boolean
  limit?: number
}

export type RoutingRule = {
  id: string
  source_kind: string
  source_filter: Record<string, unknown>
  target_chat_id: string
  topic_title_template: string | null
  initial_prompt_template: string | null
  agent_override: string | null
  enabled: boolean
  priority: number
  created_at: number
}

export function useTopics(
  chatId: string | undefined,
  options: { includeOldDone?: boolean } = {},
) {
  const qc = useQueryClient()
  const includeOldDone = options.includeOldDone === true
  const queryKey = includeOldDone
    ? ['topics', chatId, 'all'] as const
    : ['topics', chatId] as const
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const cached = chatId ? await loadLocalTopics(chatId) : null
      if (cached) {
        refreshCachedQuery(
          qc,
          queryKey,
          () => api<Topic[]>(topicsPath(chatId!, includeOldDone)),
          (topics) => saveLocalTopics(chatId!, topics),
        )
        return cached
      }
      const live = await api<Topic[]>(topicsPath(chatId!, includeOldDone))
      await saveLocalTopics(chatId!, live)
      return live
    },
    enabled: !!chatId,
    // Synchronous render of the topic strip on chat open — same logic as
    // useMessages: paint from the IDB snapshot, refresh in background.
    placeholderData: () => {
      if (!chatId) return undefined
      const snap = getSnapshotTopics(chatId)
      return snap && snap.length > 0 ? snap : undefined
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    networkMode: 'always',
    select: (topics) => topicsForMode(topics, includeOldDone),
  })
  const refetchTopics = query.refetch
  useEffect(() => {
    if (!chatId || !includeOldDone) return
    void refetchTopics()
  }, [chatId, includeOldDone, refetchTopics])
  return query
}

export function useCreateTopic(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { opening_prompt: string }) =>
      api<{ ok: boolean; id: string; title: string }>(`/api/chats/${chatId}/topics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['topics', chatId] }),
  })
}

export function useSnoozeTopic(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ topic_id, duration_s, wake_at, reason }: {
      topic_id: string
      duration_s?: number
      wake_at?: number  // unix epoch — preferred over duration_s when picking from calendar
      reason?: string
    }) =>
      api<{ ok: boolean; snooze_id: string; wake_at: number }>(
        `/api/chats/${chatId}/topics/${topic_id}/snooze`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ duration_s, wake_at, reason }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
  })
}

export function useSnoozeChat(chatId: string | undefined) {
  const qc = useQueryClient()
  type Body = { wake_at: number | null; reason?: string }
  type Result = { ok: boolean; wake_at?: number }
  const patchChat = (chat: Chat, wakeAt: number | null): Chat => ({
    ...chat,
    snoozed_until: wakeAt,
    is_snoozed: wakeAt !== null && wakeAt > Math.floor(Date.now() / 1000),
  })
  return useMutation<
    Result,
    Error,
    Body,
    { prevChats?: Chat[]; prevChat?: Chat }
  >({
    mutationFn: ({ wake_at, reason }) => {
      if (wake_at === null) {
        return api<Result>(`/api/chats/${chatId}/snooze`, { method: 'DELETE' })
      }
      return api<Result>(`/api/chats/${chatId}/snooze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wake_at, reason }),
      })
    },
    onMutate: async ({ wake_at }) => {
      if (!chatId) return {}
      await qc.cancelQueries({ queryKey: ['chats'] })
      await qc.cancelQueries({ queryKey: ['chat', chatId] })
      const prevChats = qc.getQueryData<Chat[]>(['chats'])
      const prevChat = qc.getQueryData<Chat>(['chat', chatId])
      qc.setQueryData<Chat[]>(['chats'], (cur) =>
        cur?.map((chat) => (chat.id === chatId ? patchChat(chat, wake_at) : chat)),
      )
      qc.setQueryData<Chat>(['chat', chatId], (cur) =>
        cur ? patchChat(cur, wake_at) : cur,
      )
      return { prevChats, prevChat }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevChats) qc.setQueryData(['chats'], ctx.prevChats)
      if (ctx?.prevChat && chatId) qc.setQueryData(['chat', chatId], ctx.prevChat)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['chats'] })
      if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
    },
  })
}

/* ── Round 3: topic management hooks ──────────────────────────────────── */

export function useUpdateTopic(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ topic_id, title, closed, preferred_model, claude_effort, codex_model, codex_effort }: {
      topic_id: string
      title?: string
      closed?: boolean
      preferred_model?: WritableAgent
      claude_effort?: ClaudeEffort
      codex_model?: CodexModel
      codex_effort?: CodexEffort
    }) =>
      api<{ ok: boolean }>(`/api/chats/${chatId}/topics/${topic_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, closed, preferred_model, claude_effort, codex_model, codex_effort }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['topics', chatId] }),
  })
}

/** Close a topic (= "Mark done"). Defaults to ALSO closing the source
 * Todoist task with a 6-second deferral so the undo toast can intercept. */
export function useCloseTopic(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ topic_id, closeTodoist = true }: { topic_id: string; closeTodoist?: boolean }) =>
      api<{ ok: boolean; source_task_id: string | null; deferred_seconds: number }>(
        `/api/chats/${chatId}/topics/${topic_id}?close_todoist=${closeTodoist ? 1 : 0}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
    },
  })
}

export function useCloseTopics(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ topic_ids, closeTodoist = true }: { topic_ids: string[]; closeTodoist?: boolean }) =>
      api<{
        ok: boolean
        closed_topic_ids: string[]
        already_closed_topic_ids: string[]
        deferred_seconds: number
      }>(
        `/api/chats/${chatId}/topics/bulk-close?close_todoist=${closeTodoist ? 1 : 0}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ topic_ids }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
  })
}

/** Close only the AgentChat topic and block future respawns for its source
 * Todoist task. The Todoist task stays open. */
export function useBlockTopicSource(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ topic_id }: { topic_id: string }) =>
      api<{ ok: boolean; source_task_id: string }>(
        `/api/chats/${chatId}/topics/${topic_id}/block-source`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
  })
}

/** Undo the most recent close. If still within the 6s deferral window the
 * Todoist task is never closed; otherwise we call /tasks/{id}/reopen. */
export function useReopenTopic(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ topic_id, reopenTodoist = true }: { topic_id: string; reopenTodoist?: boolean }) =>
      api<{ ok: boolean; marker_was_pending: boolean }>(
        `/api/chats/${chatId}/topics/${topic_id}/reopen?reopen_todoist=${reopenTodoist ? 1 : 0}`,
        { method: 'POST' },
      ),
    onSuccess: (_data, { topic_id }) => {
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
      qc.invalidateQueries({ queryKey: ['topic-source', topic_id] })
    },
  })
}

export function useResetChat(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    onMutate: async () => {
      if (!chatId) return
      const now = Math.floor(Date.now() / 1000)
      const tempId = `reset-${now}`
      await Promise.all([
        qc.cancelQueries({ queryKey: ['messages', chatId] }),
        qc.cancelQueries({ queryKey: ['chat', chatId] }),
        qc.cancelQueries({ queryKey: ['chats'] }),
      ])
      const previousMessages = qc.getQueryData<Message[]>(['messages', chatId])
      const previousChat = qc.getQueryData<Chat>(['chat', chatId])
      const previousChats = qc.getQueryData<Chat[]>(['chats'])
      const resetMessage: Message = {
        id: tempId,
        topic_id: null,
        external_message_id: null,
        direction: 'system',
        user: 'system',
        ts: now,
        body: 'Resetting chat...',
        status: 'received',
        queue_position: null,
        force_dispatch: false,
        reply_to_message_id: null,
        unread: false,
        deleted: false,
      }
      qc.setQueryData<Message[]>(['messages', chatId], (prev) => [...(prev || []), resetMessage])
      qc.setQueryData<Chat>(['chat', chatId], (prev) => prev ? {
        ...prev,
        last_reset_at: now,
        last_activity_at: now,
        last_agent_reply_at: null,
        session_title: null,
        lane_status: 'starting',
        queue_depth: 0,
        active_work_depth: 0,
        unread: 0,
      } : prev)
      qc.setQueryData<Chat[]>(['chats'], (prev) => prev?.map((chat) => (
        chat.id === chatId
          ? {
              ...chat,
              last_reset_at: now,
              last_activity_at: now,
              session_title: null,
              lane_status: 'starting',
              queue_depth: 0,
              active_work_depth: 0,
              unread: 0,
            }
          : chat
      )))
      return { previousMessages, previousChat, previousChats, tempId }
    },
    mutationFn: () => api<{ ok: boolean; reset_at: number; message_id: string; body: string }>(
      `/api/chats/${chatId}/reset`,
      { method: 'POST' },
    ),
    onError: (_err, _vars, ctx) => {
      if (!chatId || !ctx) return
      qc.setQueryData(['messages', chatId], ctx.previousMessages)
      qc.setQueryData(['chat', chatId], ctx.previousChat)
      qc.setQueryData(['chats'], ctx.previousChats)
    },
    onSuccess: (data, _vars, ctx) => {
      if (!chatId) return
      qc.setQueryData<Message[]>(['messages', chatId], (prev) => {
        const list = prev || []
        const nextMessage: Message = {
          id: data.message_id,
          topic_id: null,
          external_message_id: null,
          direction: 'system',
          user: 'system',
          ts: data.reset_at,
          body: data.body,
          status: 'received',
          queue_position: null,
          force_dispatch: false,
          reply_to_message_id: null,
          pinned: false,
          unread: false,
          deleted: false,
        }
        const idx = ctx?.tempId ? list.findIndex((m) => m.id === ctx.tempId) : -1
        if (idx >= 0) {
          const copy = list.slice()
          copy[idx] = nextMessage
          return copy
        }
        return [...list, nextMessage]
      })
      qc.setQueryData<Chat>(['chat', chatId], (prev) => prev ? {
        ...prev,
        last_reset_at: data.reset_at,
        last_activity_at: data.reset_at,
        last_agent_reply_at: null,
        session_title: null,
        lane_status: 'starting',
        queue_depth: 0,
        active_work_depth: 0,
        unread: 0,
      } : prev)
      qc.setQueryData<Chat[]>(['chats'], (prev) => prev?.map((chat) => (
        chat.id === chatId
          ? {
              ...chat,
              last_reset_at: data.reset_at,
              last_activity_at: data.reset_at,
              session_title: null,
              lane_status: 'starting',
              queue_depth: 0,
              active_work_depth: 0,
              unread: 0,
            }
          : chat
      )))
    },
    onSettled: () => {
      if (chatId) {
        clearExpandedMessageReadModel(chatId, null)
        qc.invalidateQueries({ queryKey: messagesKey(chatId, null), exact: true })
      }
      qc.invalidateQueries({ queryKey: ['chat', chatId] })
      qc.invalidateQueries({ queryKey: ['chats'] })
      qc.invalidateQueries({ queryKey: ['lanes'] })
    },
  })
}

/**
 * Mark every unread message in this chat as read.
 *
 * Called from ChatPage on mount. The cache is patched optimistically so
 * the chat list badge is gone the moment Mark hits back — no waiting on
 * the round-trip. On error we roll back; targeted foreground/reconnect/WS
 * refreshes reconcile later.
 *
 * We patch BOTH caches:
 *   - ['chats']: clear the matching unread count without touching sibling topics.
 *   - ['messages', chatId, topicId]: zero `m.unread` on matching cached messages.
 *
 * The messages-cache patch keeps the cached snapshot in step with what
 * the server is about to do. Without it, the per-message `unread:true`
 * flags survive forever (mark-read only sets unread=0 server-side, no
 * WS event invalidates the messages query for chat_read), so every
 * subsequent open of this chat sees a stale snapshot where every
 * message still looks unread — which trips the smart-keyboard logic in
 * ChatPage into the wrong autofocus decision.
 */
type MarkReadVars = {
  topicId?: string | null
  allTopics?: boolean
  reconcileMessages?: boolean
}

type MarkReadContext = {
  prevChats?: Chat[]
  prevTopics?: Topic[]
  prevMessages: Array<[readonly unknown[], Message[] | undefined]>
}

export function useMarkChatRead(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation<
    {
      ok: boolean
      marked_read: number
      read_at?: number
      message_scope_versions?: MessageScopeVersions
    },
    Error,
    MarkReadVars | undefined,
    MarkReadContext
  >({
    mutationFn: (vars) => {
      const params = new URLSearchParams()
      if (vars?.topicId) params.set('topic_id', vars.topicId)
      if (vars?.allTopics) params.set('all_topics', '1')
      const query = params.toString()
      return api<{ ok: boolean; marked_read: number }>(
        `/api/chats/${chatId}/mark-read${query ? `?${query}` : ''}`,
        { method: 'POST' },
      )
    },
    onMutate: async (vars) => {
      const topicId = vars?.topicId ?? null
      const allTopics = vars?.allTopics === true
      if (!chatId) return { prevMessages: [] }
      await qc.cancelQueries({ queryKey: ['chats'] })
      await qc.cancelQueries({ queryKey: ['topics', chatId] })
      await qc.cancelQueries({ queryKey: ['messages', chatId] })
      const prevChats = qc.getQueryData<Chat[]>(['chats'])
      const prevTopics = qc.getQueryData<Topic[]>(['topics', chatId])
      const prevMessages = qc.getQueriesData<Message[]>({ queryKey: ['messages', chatId] })
      applyReadStateToQueryCache(qc, chatId, { topicId, allTopics })
      return { prevChats, prevTopics, prevMessages }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevChats) qc.setQueryData(['chats'], ctx.prevChats)
      if (ctx?.prevTopics && chatId) qc.setQueryData(['topics', chatId], ctx.prevTopics)
      for (const [queryKey, data] of ctx?.prevMessages ?? []) qc.setQueryData(queryKey, data)
    },
    onSuccess: (data, vars) => {
      if (!chatId) return
      const topicId = vars?.topicId ?? null
      const allTopics = vars?.allTopics === true
      applyReadReceiptToClient(
        qc,
        chatId,
        { topicId, allTopics, markedRead: data.marked_read },
        data.message_scope_versions,
        { reconcileMessages: vars?.reconcileMessages !== false && !allTopics },
      )
    },
    // Reconcile metadata after mark-read settles. Open chat pages pass
    // reconcileMessages:false because the optimistic unread patch plus WS
    // reply events own the visible transcript; broad message invalidation
    // swaps the mounted virtualized list and causes scroll jumps.
    onSettled: (_data, _error, vars) => {
      qc.invalidateQueries({ queryKey: ['chats'] })
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
      qc.invalidateQueries({ queryKey: ['chat', chatId] })
      if (vars?.reconcileMessages !== false) {
        refreshMessageReadModel(qc, chatId, vars?.topicId ?? null)
      }
    },
  })
}

export function useAskAnother(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      agent: AskAnotherAgent
      prompt: string
      topic_id?: string | null
    }) =>
      api<{
        ok: boolean
        message_id?: string
        response_preview?: string
        handoff?: ChatGptProHandoff
      }>(
        body.agent === 'pro'
          ? `/api/chats/${chatId}/pro-handoff`
          : `/api/chats/${chatId}/ask-another`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            body.agent === 'pro'
              ? { prompt: body.prompt, topic_id: body.topic_id }
              : body,
          ),
        },
      ),
    onSuccess: (data, vars) => {
      if (!data.handoff) refreshMessageReadModel(qc, chatId, vars.topic_id ?? null)
    },
  })
}

export function usePinChat(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pinned: boolean) =>
      api<{ ok: boolean }>(`/api/chats/${chatId}/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinned }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chats'] }),
  })
}

export function useUpdateChatSessionTitle(chatId: string | undefined) {
  const qc = useQueryClient()
  type Context = { prevChats?: Chat[]; prevChat?: Chat }
  const patchTitle = (chat: Chat, sessionTitle: string | null): Chat => ({
    ...chat,
    session_title: sessionTitle,
  })
  return useMutation<{ ok: boolean }, Error, string | null, Context>({
    mutationFn: (sessionTitle) =>
      api<{ ok: boolean }>(`/api/chats/${chatId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_title: sessionTitle }),
      }),
    onMutate: async (sessionTitle) => {
      if (!chatId) return {}
      const normalizedTitle = sessionTitle?.trim() || null
      await qc.cancelQueries({ queryKey: ['chats'] })
      await qc.cancelQueries({ queryKey: ['chat', chatId] })
      const prevChats = qc.getQueryData<Chat[]>(['chats'])
      const prevChat = qc.getQueryData<Chat>(['chat', chatId])
      qc.setQueryData<Chat[]>(['chats'], (cur) =>
        cur?.map((chat) => (chat.id === chatId ? patchTitle(chat, normalizedTitle) : chat)),
      )
      qc.setQueryData<Chat>(['chat', chatId], (cur) =>
        cur ? patchTitle(cur, normalizedTitle) : cur,
      )
      return { prevChats, prevChat }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevChats) qc.setQueryData(['chats'], ctx.prevChats)
      if (ctx?.prevChat && chatId) qc.setQueryData(['chat', chatId], ctx.prevChat)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['chats'] })
      if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
    },
  })
}

/**
 * Toggle per-chat OS push notifications. WhatsApp-style mute: in-app
 * unread / badge state is unaffected (Mark still sees that the chat
 * has activity), but no push notification fires for messages in the
 * muted chat. Approval requests are exempt server-side — those always
 * push, mute or not.
 *
 * Used for chats that are noisy (X-Links firehose) or under-development
 * groups Mark hasn't started using yet.
 */
export function useMuteChat(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (muted: boolean) =>
      api<{ ok: boolean }>(`/api/chats/${chatId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ muted }),
      }),
    onMutate: async (muted) => {
      if (!chatId) return {}
      await qc.cancelQueries({ queryKey: ['chats'] })
      await qc.cancelQueries({ queryKey: ['chat', chatId] })
      const prevList = qc.getQueryData<Chat[]>(['chats'])
      qc.setQueryData<Chat[]>(['chats'], (cur) =>
        (cur ?? []).map((c) => (c.id === chatId ? { ...c, muted } : c)),
      )
      qc.setQueryData<Chat>(['chat', chatId], (cur) =>
        cur ? { ...cur, muted } : cur,
      )
      return { prevList }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevList) qc.setQueryData(['chats'], ctx.prevList)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['chats'] })
      if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
    },
  })
}

export function useMuteTopicStartNotifications(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mute_topic_start_notifications: boolean) =>
      api<{ ok: boolean }>(`/api/chats/${chatId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mute_topic_start_notifications }),
      }),
    onMutate: async (mute_topic_start_notifications) => {
      if (!chatId) return {}
      await qc.cancelQueries({ queryKey: ['chats'] })
      await qc.cancelQueries({ queryKey: ['chat', chatId] })
      const prevList = qc.getQueryData<Chat[]>(['chats'])
      qc.setQueryData<Chat[]>(['chats'], (cur) =>
        (cur ?? []).map((c) =>
          c.id === chatId ? { ...c, mute_topic_start_notifications } : c,
        ),
      )
      qc.setQueryData<Chat>(['chat', chatId], (cur) =>
        cur ? { ...cur, mute_topic_start_notifications } : cur,
      )
      return { prevList }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevList) qc.setQueryData(['chats'], ctx.prevList)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['chats'] })
      if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
    },
  })
}

export function useForceNow(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (message_id: string) =>
      api<{
        ok: boolean
        message_id?: string
        new_position?: number
        force_dispatch?: boolean
        blocked_by_current_turn?: boolean
        steered?: boolean
        no_op?: boolean
        reason?: string
      }>(
        `/api/messages/${message_id}/force-now`,
        { method: 'POST' },
      ),
    onMutate: async (message_id: string) => {
      if (!chatId) return { previous: [] }
      await qc.cancelQueries({ queryKey: ['messages', chatId] })
      const previous = qc.getQueriesData<Message[]>({ queryKey: ['messages', chatId] })
      qc.setQueriesData<Message[]>({ queryKey: ['messages', chatId] }, (cur) =>
        cur?.map((m) => (
          m.id === message_id
            ? { ...m, queue_position: 0, force_dispatch: true }
            : m
        )),
      )
      return { previous }
    },
    onError: (_err, _messageId, ctx) => {
      ctx?.previous?.forEach(([key, value]) => {
        qc.setQueryData(key, value)
      })
    },
    onSuccess: (data, message_id) => {
      if (data.steered && chatId) {
        qc.setQueriesData<Message[]>({ queryKey: ['messages', chatId] }, (cur) =>
          cur?.map((m) => (
            m.id === message_id
              ? { ...m, status: 'dispatched', queue_position: null, force_dispatch: true }
              : m
          )),
        )
      }
      refreshRetainedMessageReadModels(qc)
      qc.invalidateQueries({ queryKey: ['chats'] })
      if (chatId) qc.invalidateQueries({ queryKey: ['topics', chatId] })
    },
  })
}

export type StopTurnResult = {
  ok: boolean
  /** True only when the provider acknowledged abandoning the turn. A finished
   *  turn or a provider with no interruptible session comes back 200 with
   *  stopped:false and a reason — not an HTTP error. */
  stopped?: boolean
  acknowledged?: boolean
  reason?: 'no_active_turn' | 'not_stoppable' | 'interrupt_failed' | string
  via?: string | null
  source_message_id?: string | null
}

export function useStopTurn(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (topic_id: string | null) =>
      api<StopTurnResult>(
        topic_id
          ? `/api/chats/${chatId}/topics/${topic_id}/stop`
          : `/api/chats/${chatId}/stop`,
        { method: 'POST', keepalive: true },
      ),
    onSettled: () => {
      // The lane clears active_turns and journals turn_ended asynchronously,
      // so refetch rather than trusting the response to have raced it.
      if (chatId) qc.invalidateQueries({ queryKey: ['topics', chatId] })
      if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
  })
}

export type QueuedMessageMutationResult = {
  ok: boolean
  message_id: string
  chat_id: string
  topic_id: string | null
  body: string
  queue_position: number
}

export function useCancelQueuedMessage(_chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (message_id: string) =>
      api<QueuedMessageMutationResult>(`/api/messages/${message_id}/queued`, {
        method: 'DELETE',
      }),
    onSuccess: (data) => {
      refreshMessageReadModel(qc, data.chat_id, data.topic_id)
      if (data.topic_id) refreshMessageReadModel(qc, data.chat_id, null)
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
  })
}

export function useRetryFailedMessage(chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (message_id: string) =>
      api<QueuedMessageMutationResult>(`/api/messages/${message_id}/retry`, {
        method: 'POST',
      }),
    onSuccess: (data) => {
      refreshMessageReadModel(qc, data.chat_id, data.topic_id)
      if (data.topic_id) refreshMessageReadModel(qc, data.chat_id, null)
      qc.invalidateQueries({ queryKey: ['chats'] })
      if (chatId) qc.invalidateQueries({ queryKey: ['chat', chatId] })
    },
  })
}

export function useEditQueuedMessage(_chatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ message_id, text }: { message_id: string; text: string }) =>
      api<QueuedMessageMutationResult>(`/api/messages/${message_id}/queued`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      }),
    onSuccess: (data) => {
      refreshMessageReadModel(qc, data.chat_id, data.topic_id)
      if (data.topic_id) refreshMessageReadModel(qc, data.chat_id, null)
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
  })
}

export type ForkMessageResult = {
  ok: boolean
  mode: 'chat' | 'topic'
  source_chat_id: string
  source_topic_id: string | null
  source_message_id: string
  target_chat_id: string
  target_topic_id: string | null
  queued_message_id: string | null
  copied_message_count: number
  session_title: string | null
}

export function useForkMessage(sourceChatId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      message_id: string
      target_chat_id?: string | null
      target_topic_title?: string | null
    }) =>
      api<ForkMessageResult>(`/api/messages/${body.message_id}/fork`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_chat_id: body.target_chat_id ?? null,
          target_topic_title: body.target_topic_title ?? null,
        }),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['chats'] })
      qc.invalidateQueries({ queryKey: ['chat', data.target_chat_id] })
      refreshMessageReadModel(qc, data.target_chat_id, data.target_topic_id)
      if (data.target_topic_id) {
        qc.invalidateQueries({ queryKey: ['topics', data.target_chat_id] })
      }
      if (sourceChatId) {
        qc.invalidateQueries({ queryKey: ['chat', sourceChatId] })
      }
    },
  })
}

export function useSkills(query: string = '', limit: number = 40) {
  const cached = useMemo(() => readCachedSkills(), [])
  const result = useQuery({
    queryKey: SKILLS_QUERY_KEY,
    queryFn: fetchAllSkills,
    initialData: cached?.skills,
    initialDataUpdatedAt: cached?.updatedAt,
    staleTime: 10 * 60 * 1000,
    gcTime: 24 * 60 * 60_000,
  })
  const data = useMemo(
    () => filterSkills(result.data ?? [], query, limit),
    [limit, query, result.data],
  )
  return {
    ...result,
    data,
    isLoading: result.isLoading && data.length === 0,
  }
}

export function useSkillDoc(skillName: string | null, enabled = true) {
  return useQuery({
    queryKey: ['skill-doc', skillName],
    queryFn: () => api<SkillDoc>(`/api/skills/${encodeURIComponent(skillName!)}/doc`),
    enabled: !!skillName && enabled,
    staleTime: 10 * 60 * 1000,
  })
}

export function useSkillIndex() {
  return useQuery({
    queryKey: SKILL_INDEX_QUERY_KEY,
    queryFn: () => api<SkillIndexResponse>('/api/skills/index'),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60_000,
  })
}

export function useSearch(
  q: string,
  scope: { chatId?: string | null; topicId?: string | null } = {},
  filters: SearchFilters = {},
  options: SearchOptions = {},
) {
  const chatId = scope.chatId || null
  const topicId = scope.topicId || null
  const author = filters.author || 'all'
  const fromTs = filters.fromTs ?? null
  const toTs = filters.toTs ?? null
  const enabled = options.enabled !== false
  const limit = options.limit ?? 80
  return useQuery({
    queryKey: ['search', q, chatId, topicId, author, fromTs, toTs, limit],
    queryFn: () => {
      const params = new URLSearchParams({ q, limit: String(limit) })
      if (chatId) params.set('chat_id', chatId)
      if (topicId) params.set('topic_id', topicId)
      if (author !== 'all') params.set('author', author)
      if (fromTs !== null) params.set('from_ts', String(fromTs))
      if (toTs !== null) params.set('to_ts', String(toTs))
      return api<SearchHit[]>(`/api/search?${params.toString()}`)
    },
    enabled: enabled && q.length >= 2,
    staleTime: 30_000,
  })
}

export function useRoutingRules() {
  return useQuery({
    queryKey: ['routing-rules'],
    queryFn: () => api<RoutingRule[]>('/api/routing-rules'),
  })
}

// ── Codex account switch ────────────────────────────────────────────────────
// One global read model for the header chip and the Settings toggle. The
// `codex_account_changed` journal kind invalidates it (ws.ts); the interval
// only covers a missed event.

export const CODEX_AVAILABILITY_QUERY_KEY = ['codex-availability'] as const

export function useCodexAvailability(enabled = true) {
  return useQuery({
    queryKey: CODEX_AVAILABILITY_QUERY_KEY,
    queryFn: () => api<CodexAvailability>('/api/codex-availability'),
    enabled,
    staleTime: 30_000,
    refetchInterval: 120_000,
  })
}

export function useSetCodexAccountMode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mode: CodexAccountMode) =>
      api<CodexAvailability>('/api/codex-availability/mode', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(CODEX_AVAILABILITY_QUERY_KEY, data)
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
  })
}

export function useCloseSourceTodoist(chatId: string | undefined) {
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean; todoist_task_id: string }>(
        `/api/chats/${chatId}/close-source-todoist`, { method: 'POST' }),
  })
}
